import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { EmailService } from '../services/emailService'

function ProjectProgress({ projectId, fallbackPercent }) {
  const [percent, setPercent] = useState(fallbackPercent)

  useEffect(() => {
    let isMounted = true
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('tasks')
          .select('id,status')
          .eq('project_id', projectId)
        if (!error && data && isMounted) {
          const total = data.length
          const completed = data.filter(t => t.status === 'completed').length
          const pct = total > 0 ? Math.round((completed / total) * 100) : 0
          setPercent(pct)
        }
      } catch {}
    }
    load()
    return () => { isMounted = false }
  }, [projectId])

  return (
    <div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div className="bg-primary-600 h-3 rounded-full" style={{ width: `${percent}%` }}></div>
      </div>
      <p className="text-sm text-gray-500 mt-1">{percent}% Complete</p>
    </div>
  )
}
import { useAuthStore } from '../store/useAuthStore'
import Header from '../components/Header'

const AdminDashboard = () => {
  const { user, profile, signOut } = useAuthStore()
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Data states
  const [users, setUsers] = useState([])
  const [products, setProducts] = useState([])
  const [orders, setOrders] = useState([])
  const [projects, setProjects] = useState([])
  const [invoices, setInvoices] = useState([])
  const [appointments, setAppointments] = useState([])
  const [appointmentsChecked, setAppointmentsChecked] = useState(false)
  const [supportTickets, setSupportTickets] = useState([])
  const [replyingTicket, setReplyingTicket] = useState(null)
  const [replyMessage, setReplyMessage] = useState('')
  const [supportMessages, setSupportMessages] = useState([])

  // Public Live Chat states
  const [publicChats, setPublicChats] = useState([])
  const [selectedPublicChat, setSelectedPublicChat] = useState(null)
  const [publicChatMessages, setPublicChatMessages] = useState([])
  const [publicChatReply, setPublicChatReply] = useState('')
  const [publicChatLoading, setPublicChatLoading] = useState(false)
  const [publicChatUploading, setPublicChatUploading] = useState(false)
  const publicFileInputRef = React.useRef(null)
  const publicMessagesEndRef = React.useRef(null)

  const loadMessagesFor = async (ticket) => {
    if (!ticket) { setSupportMessages([]); return }
    try {
      const { data: msgs } = await supabase.rpc('st_list_messages', { p_ticket_id: ticket.id })
      setSupportMessages(msgs || [])
    } catch {
      setSupportMessages([])
    }
  }

  useEffect(() => {
    if (replyingTicket) {
      loadMessagesFor(replyingTicket)
    }
  }, [replyingTicket])

  // Load public live chats list
  const loadPublicChats = async () => {
    try {
      const { data, error } = await supabase.rpc('plc_list_chats')
      if (!error) setPublicChats(data || [])
    } catch (e) {
      setPublicChats([])
    }
  }

  // Load messages for a given public chat
  const loadPublicChatMessages = async (chatId) => {
    if (!chatId) return
    setPublicChatLoading(true)
    try {
      const { data, error } = await supabase.rpc('plc_list_messages', { p_chat_id: chatId })
      if (!error) setPublicChatMessages(data || [])
      else setPublicChatMessages([])
    } catch (e) {
      setPublicChatMessages([])
    } finally {
      setPublicChatLoading(false)
    }
  }

  // Send admin reply to public chat
  const sendPublicChatReply = async (e) => {
    e.preventDefault()
    if (!selectedPublicChat || !publicChatReply.trim()) return
    const msg = publicChatReply.trim()
    setPublicChatReply('')
    try {
      const { error } = await supabase.rpc('plc_add_message', { p_chat_id: selectedPublicChat.id, p_sender: 'admin', p_message: msg })
      if (error) throw error
      await loadPublicChatMessages(selectedPublicChat.id)
      await loadPublicChats()
    } catch (err) {
      alert(`Failed to send: ${err.message || err}`)
    }
  }

  const onAdminAttachClick = () => {
    if (!selectedPublicChat?.id) { alert('Select a chat first.'); return }
    publicFileInputRef.current?.click()
  }

  const uploadAdminAttachment = async (file) => {
    if (!file || !selectedPublicChat?.id) return
    const max = 20 * 1024 * 1024
    if (file.size > max) { alert('File too large (max 20MB).'); return }
    try {
      setPublicChatUploading(true)
      const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_')
      const path = `${selectedPublicChat.id}/admin-${Date.now()}-${safeName}`
      const { error: upErr } = await supabase.storage
        .from('public-chat-attachments')
        .upload(path, file, { cacheControl: '3600', upsert: false })
      if (upErr) throw upErr
      const { data: pub } = supabase.storage
        .from('public-chat-attachments')
        .getPublicUrl(path)
      const url = pub?.publicUrl
      if (!url) throw new Error('Failed to get public URL')
      const { error } = await supabase.rpc('plc_add_message', { p_chat_id: selectedPublicChat.id, p_sender: 'admin', p_message: url })
      if (error) throw error
      await loadPublicChatMessages(selectedPublicChat.id)
      await loadPublicChats()
    } catch (err) {
      console.error('Admin upload failed:', err)
      if ((err?.message || '').toLowerCase().includes('bucket')) {
        alert('Storage bucket missing. Create "public-chat-attachments" with public read.')
      } else {
        alert(err.message || 'Upload failed')
      }
    } finally {
      setPublicChatUploading(false)
      if (publicFileInputRef.current) publicFileInputRef.current.value = ''
    }
  }

  // Auto scroll to bottom of admin live chat thread on new messages
  useEffect(() => {
    try { publicMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) } catch {}
  }, [publicChatMessages, selectedPublicChat])

  const getInitial = (nameOrEmail) => {
    if (!nameOrEmail) return 'U'
    const s = (nameOrEmail || '').toString().trim()
    const letter = s[0] || 'U'
    return letter.toUpperCase()
  }

  // Initialize live chat data when entering the tab
  useEffect(() => {
    if (activeTab === 'livechat') {
      loadPublicChats()
    }
  }, [activeTab])

  // Manual refresh helper for the UI button
  const refreshLiveChat = async () => {
    await loadPublicChats()
    if (selectedPublicChat?.id) {
      await loadPublicChatMessages(selectedPublicChat.id)
    }
  }

  // Realtime: subscribe to public_chats and public_chat_messages
  useEffect(() => {
    if (activeTab !== 'livechat') return

    const channel = supabase
      .channel('admin-livechat-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'public_chats' }, () => {
        loadPublicChats()
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'public_chats' }, () => {
        loadPublicChats()
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'public_chat_messages' }, (payload) => {
        // If this message belongs to the opened chat, refresh thread; always refresh list timestamps/status
        if (selectedPublicChat && payload?.new?.chat_id === selectedPublicChat.id) {
          loadPublicChatMessages(selectedPublicChat.id)
        }
        loadPublicChats()
      })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [activeTab, selectedPublicChat?.id])
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false)
  const [creatingAppointment, setCreatingAppointment] = useState(false)
  const [newAppointment, setNewAppointment] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time_slot: '',
    message: '',
    status: 'pending'
  })
  const [availableAdminSlots, setAvailableAdminSlots] = useState([])

  const generateAdminSlots = (dateStr, durationMinutes = 60) => {
    if (!dateStr) return []
    const d = new Date(dateStr + 'T00:00:00')
    if (d.getDay() === 5) return [] // Friday skip
    const out = []
    let start = new Date(dateStr + 'T10:00:00')
    const end = new Date(dateStr + 'T17:00:00')
    while (start <= end) {
      const hh = String(start.getHours()).padStart(2, '0')
      const mm = String(start.getMinutes()).padStart(2, '0')
      out.push(`${hh}:${mm}`)
      start = new Date(start.getTime() + durationMinutes * 60000)
    }
    return out
  }

  useEffect(() => {
    ;(async () => {
      const dateStr = newAppointment.date
      if (!dateStr) { setAvailableAdminSlots([]); return }
      try {
        const { data, error } = await supabase
          .from('appointments')
          .select('time_slot')
          .eq('date', dateStr)
          .in('status', ['pending','confirmed'])
        if (error) throw error
        const booked = (data||[]).map(r => r.time_slot)
        const all = generateAdminSlots(dateStr, 60)
        setAvailableAdminSlots(all.filter(s => !booked.includes(s)))
      } catch (e) {
        setAvailableAdminSlots(generateAdminSlots(dateStr, 60))
      }
    })()
  }, [newAppointment.date, newAppointment.service])
  const [dataFetched, setDataFetched] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [viewInvoice, setViewInvoice] = useState(null)
  const [creatingInvoice, setCreatingInvoice] = useState(false)
  const [showManualInvoiceModal, setShowManualInvoiceModal] = useState(false)
  const [creatingManualInvoice, setCreatingManualInvoice] = useState(false)
  const [manualInvoices, setManualInvoices] = useState([])
  const [invoiceView, setInvoiceView] = useState('project') // 'project' | 'manual'
  const [servicesList, setServicesList] = useState([])
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [editingService, setEditingService] = useState(null)
  const [savingService, setSavingService] = useState(false)
  const [serviceForm, setServiceForm] = useState({ name: '', description: '', icon: '' })
  const [savingSettings, setSavingSettings] = useState(false)
  const [settingsData, setSettingsData] = useState(null)
  const [contactMessages, setContactMessages] = useState([])
  const [showContactModal, setShowContactModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  
  // Plain-text paste helpers to neutralize rich/extension-injected content
  const normalizeTextInput = (t) => {
    let str = (t || '').toString()
      .normalize('NFKC')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ')
      .replace(/[\r\t]+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim()
    str = str.replace(/[^\x09\x0A\x0D\x20-\x7E]+/g, '')
    return str.length > 20000 ? str.slice(0, 20000) : str
  }
  const sanitizeUrlInput = (u) => {
    let s = (u || '').toString().normalize('NFKC')
    s = s.replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ').trim()
    if (!s) return ''
    s = s.replace(/\s+/g, '%20')
    try {
      const url = new URL(s)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''
      let out = url.toString()
      if (out.length > 512) out = `${url.origin}${url.pathname}`
      return out
    } catch { return '' }
  }
  const normalizePriceInput = (p) => {
    const cleaned = (p ?? '').toString().replace(/[^0-9.]/g, '')
    return cleaned
  }
  const handlePastePlainText = (setState, field, type = 'text') => async (e) => {
    try {
      e.preventDefault()
      let txt = e.clipboardData?.getData('text')
      if (txt == null) {
        try { txt = await navigator.clipboard.readText() } catch {}
      }
      if (txt == null) txt = ''
      let value = type === 'url' ? sanitizeUrlInput(txt)
                 : type === 'price' ? normalizePriceInput(txt)
                 : normalizeTextInput(txt)
      setState(prev => ({ ...prev, [field]: value }))
    } catch {}
  }

  // For uncontrolled inputs (forms using FormData), paste directly into the element value
  const handlePasteToInput = (type = 'text') => async (e) => {
    try {
      e.preventDefault()
      let txt = e.clipboardData?.getData('text')
      if (txt == null) {
        try { txt = await navigator.clipboard.readText() } catch {}
      }
      if (txt == null) txt = ''
      let value = type === 'url' ? sanitizeUrlInput(txt)
                 : type === 'price' ? normalizePriceInput(txt)
                 : normalizeTextInput(txt)
      e.currentTarget.value = value
      const evt = new Event('input', { bubbles: true })
      e.currentTarget.dispatchEvent(evt)
    } catch {}
  }

  // Normalize values on any input change (helps when extensions modify value without paste)
  const handleNormalizeOnInput = (type = 'text') => (e) => {
    try {
      const raw = e.currentTarget.value
      let value = type === 'url' ? sanitizeUrlInput(raw)
                 : type === 'price' ? normalizePriceInput(raw)
                 : normalizeTextInput(raw)
      if (value !== raw) {
        e.currentTarget.value = value
        const evt = new Event('input', { bubbles: true })
        e.currentTarget.dispatchEvent(evt)
      }
    } catch {}
  }

  // Emoji-specific handlers: keep at most two visible characters, allow emojis
  const handleEmojiPaste = (setState, field) => async (e) => {
    try {
      e.preventDefault()
      let txt = e.clipboardData?.getData('text')
      if (txt == null) {
        try { txt = await navigator.clipboard.readText() } catch {}
      }
      if (txt == null) txt = ''
      const value = Array.from(txt).slice(0, 2).join('')
      setState(prev => ({ ...prev, [field]: value }))
    } catch {}
  }
  const handleEmojiInput = (setState, field) => (e) => {
    try {
      const raw = e.currentTarget.value
      const limited = Array.from(raw).slice(0, 2).join('')
      if (limited !== raw) {
        e.currentTarget.value = limited
        const evt = new Event('input', { bubbles: true })
        e.currentTarget.dispatchEvent(evt)
      }
      setState(prev => ({ ...prev, [field]: limited }))
    } catch {}
  }

  // Load company settings when opening Settings tab
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('company_settings')
          .select('*')
          .eq('id', 'global')
          .maybeSingle()
        if (!error) setSettingsData(data || null)
      } catch (e) {
        console.warn('Settings fetch failed:', e)
      }
    }
    if (activeTab === 'settings') loadSettings()
  }, [activeTab])

  // Load contact messages when opening tab
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const { data, error } = await supabase
          .from('contact_messages')
          .select('*')
          .order('created_at', { ascending: false })
        if (!error && data) {
          setContactMessages(data)
        }
      } catch (e) {
        console.warn('Contact messages fetch failed:', e)
      }
    }
    if (activeTab === 'contacts') loadContacts()
  }, [activeTab])

  const refreshContacts = async () => {
    const { data } = await supabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false })
    setContactMessages(data || [])
  }

  const handleViewContact = (msg) => {
    setSelectedContact(msg)
    setShowContactModal(true)
  }

  const handleMarkReplied = async (msg) => {
    const { error } = await supabase
      .from('contact_messages')
      .update({ status: 'replied' })
      .eq('id', msg.id)
    if (error) {
      alert(`Failed to mark replied: ${error.message}`)
      return
    }
    await refreshContacts()
    alert('Marked as replied')
  }

  const handleDeleteContact = async (msg) => {
    if (!confirm('Delete this message?')) return
    const { error } = await supabase
      .from('contact_messages')
      .delete()
      .eq('id', msg.id)
    if (error) {
      alert(`Delete failed: ${error.message}`)
      return
    }
    await refreshContacts()
    alert('Deleted')
  }

  const handleViewInvoice = (inv) => {
    setViewInvoice(inv)
    setShowInvoiceModal(true)
  }

  const handleViewManualInvoice = (mi) => {
    // Normalize to view in same modal using common fields
    const normalized = {
      ...mi,
      subject: mi.subject || mi.product_subject || '',
      client_name: mi.client_name,
      project_name: mi.project_name,
    }
    setViewInvoice(normalized)
    setShowInvoiceModal(true)
  }

  const handleDownloadInvoice = async (inv) => {
    try {
      const { generateInvoicePdf } = await import('../utils/invoicePdf')
      await generateInvoicePdf({
        invoice: inv,
        project: { id: inv.project_id, name: inv.project_name },
        client: { id: inv.client_id, name: inv.client_name }
      })
    } catch (e) {
      console.error('Invoice download failed:', e)
      alert('Failed to generate invoice PDF')
    }
  }

  const handleMarkInvoicePaid = async (inv) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update({ status: 'paid' })
        .eq('id', inv.id)
        .select()
        .single()
      if (error) {
        console.error('Failed to mark paid:', error)
        alert('Failed to update invoice status')
        return
      }
      // Update list locally
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'paid' } : i))
      // If previewing, update view state too
      setViewInvoice(v => v && v.id === inv.id ? { ...v, status: 'paid' } : v)
    } catch (e) {
      console.error('Mark paid error:', e)
      alert('Failed to update invoice')
    }
  }

  const handleDeleteInvoice = async (inv) => {
    if (!confirm(`Are you sure you want to delete invoice #${inv.id.slice(0, 8)}? This action cannot be undone.`)) {
      return
    }

    try {
      console.log('[AdminDashboard] Deleting invoice:', inv.id)
      const { data, error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', inv.id)
        .select()
      
      if (error) {
        console.error('[AdminDashboard] Delete invoice error:', error)
        alert(`Failed to delete invoice: ${error.message}\n\nIf this persists, you may need to run the RLS policies SQL in Supabase.`)
        return
      }

      console.log('[AdminDashboard] Invoice deleted successfully:', data)

      // Remove from local state
      setInvoices(prev => prev.filter(i => i.id !== inv.id))
      
      // If viewing, close modal
      if (viewInvoice && viewInvoice.id === inv.id) {
        setViewInvoice(null)
        setShowInvoiceModal(false)
      }

      alert('Invoice deleted successfully!')
      // Refresh invoices list
      setDataFetched(false)
      setTimeout(() => fetchAllData(), 500)
    } catch (error) {
      console.error('[AdminDashboard] Delete invoice exception:', error)
      alert(`Failed to delete invoice: ${error.message}`)
    }
  }

  const handleDeleteManualInvoice = async (inv) => {
    if (!confirm(`Are you sure you want to delete manual invoice #${inv.id.slice(0, 8)}?`)) return
    try {
      const { error } = await supabase.from('manual_invoices').delete().eq('id', inv.id)
      if (error) throw error
      setManualInvoices(prev => prev.filter(i => i.id !== inv.id))
      if (viewInvoice && viewInvoice.id === inv.id) {
        setViewInvoice(null)
        setShowInvoiceModal(false)
      }
      alert('Manual invoice deleted successfully!')
    } catch (e) {
      console.error('Delete manual invoice error:', e)
      alert(`Failed to delete manual invoice: ${e.message}`)
    }
  }

  const handleMarkManualInvoicePaid = async (inv) => {
    try {
      const { error } = await supabase
        .from('manual_invoices')
        .update({ status: 'paid' })
        .eq('id', inv.id)
      if (error) throw error
      setManualInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: 'paid' } : i))
      if (viewInvoice && viewInvoice.id === inv.id) {
        setViewInvoice(prev => prev ? { ...prev, status: 'paid' } : prev)
      }
      alert('Marked as paid')
    } catch (e) {
      console.error('Mark manual invoice paid error:', e)
      alert(`Failed to mark paid: ${e.message}`)
    }
  }

  const startEditService = (svc) => {
    setEditingService(svc)
    setServiceForm({
      name: svc?.name || '',
      description: (svc?.description || '').toString(),
      icon: svc?.icon || ''
    })
    setShowServiceModal(true)
  }

  const deleteService = async (svc) => {
    if (!window.confirm('Delete this service?')) return
    const { error } = await supabase.from('services').delete().eq('id', svc.id)
    if (!error) setServicesList(prev => prev.filter(s => s.id !== svc.id))
  }
  
  // User Management States
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [userFilter, setUserFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'client',
    phone: '',
    company: '',
    address: '',
    bio: ''
  })
  const [editUser, setEditUser] = useState({
    name: '',
    email: '',
    role: 'client',
    phone: '',
    company: '',
    address: '',
    bio: ''
  })

  // Product Management States
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [showEditProductModal, setShowEditProductModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [savingProduct, setSavingProduct] = useState(false)
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: 'website',
    is_active: true,
    image_url: '',
    live_link: '',
    specifications: '',
    key_features: '',
    third_party_link: '',
    third_party_price: '',
    html_content: ''
  })
  const [editProduct, setEditProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: 'website',
    is_active: true,
    image_url: '',
    live_link: '',
    specifications: '',
    key_features: '',
    third_party_link: '',
    third_party_price: '',
    html_content: ''
  })
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingHtml, setUploadingHtml] = useState(false)

  // Project Management States
  const [showCreateProject, setShowCreateProject] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingProjectId, setEditingProjectId] = useState(null)
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    client_id: '',
    budget: '',
    start_date: '',
    end_date: '',
    priority: 'medium',
    selectedEmployees: []
  })
  const [clients, setClients] = useState([])
  const [employees, setEmployees] = useState([])
  const [creatingProject, setCreatingProject] = useState(false)
  const [projectMembers, setProjectMembers] = useState([])
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false)
  const [editingAppointment, setEditingAppointment] = useState(null)
  const [deletingAppointmentId, setDeletingAppointmentId] = useState(null)

  useEffect(() => {
    if (user && profile?.role === 'admin' && !dataFetched) {
      fetchAllData()
    }
  }, [user, profile, dataFetched])

  const fetchAllData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Phase 1: Fetch critical data first (users needed for overview)
      console.log('🔍 Phase 1: Fetching critical data...')
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (usersError) {
        console.error('❌ Users fetch error:', usersError)
        if (usersError.code === 'PGRST205') {
          console.warn('⚠️ Profiles table not found. Please run the database setup script.')
          setError('Database tables not found. Please run the SQL setup script.')
        }
        setUsers([])
      } else {
        console.log('✅ Users fetched successfully:', usersData?.length || 0, 'users')
        setUsers(usersData || [])
        
        // Set clients and employees immediately from users data
        const clientsData = usersData?.filter(user => user.role === 'client') || []
        const employeesData = usersData?.filter(user => user.role === 'employee') || []
        setClients(clientsData)
        setEmployees(employeesData)
      }

      // Show UI immediately after critical data loads
      setLoading(false)

      // Phase 2: Fetch remaining data in parallel (non-blocking)
      console.log('🔍 Phase 2: Fetching remaining data in parallel...')
      const [
        productsResult,
        ordersResult,
        projectsResult,
        invoicesResult,
        manualInvoicesResult,
        ticketsResult,
        servicesResult,
        appointmentsResult,
        membersResult
      ] = await Promise.allSettled([
        // Products
        supabase.from('products').select('*').order('created_at', { ascending: false }),
        // Orders
        supabase.from('orders').select('*').order('created_at', { ascending: false }),
        // Projects
        supabase.from('projects').select('*').order('created_at', { ascending: false }),
        // Invoices (with joins)
        supabase.from('invoices').select('*, project:project_id(*), client:project_id(client_id)').order('created_at', { ascending: false }),
        // Manual invoices
        supabase.from('manual_invoices').select('*').order('created_at', { ascending: false }),
        // Support tickets
        supabase.rpc('st_list_all_tickets_expanded'),
        // Services
        supabase.from('services').select('*').order('created_at', { ascending: false }),
        // Appointments
        supabase.from('appointments').select('*').in('status', ['pending','confirmed']).order('date', { ascending: true }).order('time_slot', { ascending: true }),
        // Project members
        supabase.from('project_members').select('*')
      ])

      // Process results
      if (productsResult.status === 'fulfilled') {
        const { data: productsData, error: productsError } = productsResult.value
        if (!productsError) setProducts(productsData || [])
      }

      if (ordersResult.status === 'fulfilled') {
        const { data: ordersData, error: ordersError } = ordersResult.value
        if (!ordersError) setOrders(ordersData || [])
      }

      if (projectsResult.status === 'fulfilled') {
        const { data: projectsData, error: projectsError } = projectsResult.value
        if (!projectsError) setProjects(projectsData || [])
      }

      if (invoicesResult.status === 'fulfilled') {
        const { data: invoicesData, error: invoicesError } = invoicesResult.value
        if (!invoicesError && invoicesData) {
          // Enrich invoices with client profile and project name
          let enriched = invoicesData || []
          // Collect client ids directly from invoice rows
          const clientIds = [...new Set(enriched.map(i => i.client_id).filter(Boolean))]
          let clientProfiles = []
          if (clientIds.length > 0) {
            const { data: profs } = await supabase
              .from('profiles')
              .select('id,name,email')
              .in('id', clientIds)
            clientProfiles = profs || []
          }
          const parseFromNotes = (notes, label) => {
            try {
              const text = (notes || '').toString()
              const re = new RegExp(`^${label}\\s*:\\s*(.+)$`, 'im')
              const m = text.match(re)
              return m && m[1] ? m[1].trim() : ''
            } catch { return '' }
          }

          enriched = enriched.map(inv => {
            const profile = clientProfiles.find(p => p.id === inv.client_id)
            const manualClient = inv.client_name || parseFromNotes(inv.notes, 'Client')
            const manualProject = inv.project_name || parseFromNotes(inv.notes, 'Project')
            const manualSubject = inv.subject || parseFromNotes(inv.notes, 'Product/Topic|Product|Topic') || parseFromNotes(inv.notes, 'Product') || parseFromNotes(inv.notes, 'Topic')
            const manualReason = inv.reason || parseFromNotes(inv.notes, 'Reason')
            const resolvedClientName = manualClient || profile?.name || profile?.email || 'Unknown'
            const resolvedProjectName = manualProject || inv.project?.name || (inv.project_id || null)
            return {
              ...inv,
              client_name: resolvedClientName,
              project_name: resolvedProjectName,
              subject: manualSubject || inv.subject,
              reason: manualReason || inv.reason,
            }
          })
          setInvoices(enriched)
        }
      }

      if (manualInvoicesResult.status === 'fulfilled') {
        const { data: miData, error: miErr } = manualInvoicesResult.value
        if (!miErr && miData) {
          // Normalize to common fields used by UI/PDF
          const normalized = (miData || []).map(m => ({
            ...m,
            client_name: m.client_name,
            project_name: m.project_name || 'N/A (Manual Invoice)',
            subject: m.product_subject || '',
            reason: m.reason || '',
          }))
          setManualInvoices(normalized)
        }
      }

      if (ticketsResult.status === 'fulfilled') {
        const { data: tickets, error: tErr } = ticketsResult.value
        if (!tErr) setSupportTickets(tickets || [])
      }

      if (servicesResult.status === 'fulfilled') {
        const { data: svc, error: svcErr } = servicesResult.value
        if (!svcErr) setServicesList(svc || [])
      }

      if (appointmentsResult.status === 'fulfilled') {
        const { data: appts, error: apptErr } = appointmentsResult.value
        if (!apptErr) {
          setAppointments(appts || [])
        }
      }
      setAppointmentsChecked(true)

      if (membersResult.status === 'fulfilled') {
        const { data: membersData, error: membersError } = membersResult.value
        if (!membersError && membersData) {
          const userIds = membersData.map(member => member.user_id)
          const { data: userDetails, error: usersError } = await supabase
            .from('profiles')
            .select('id, name, email, role')
            .in('id', userIds)
          
          if (!usersError && userDetails) {
            const combinedData = membersData.map(member => ({
              ...member,
              user: userDetails.find(user => user.id === member.user_id)
            }))
            setProjectMembers(combinedData)
          } else {
            setProjectMembers(membersData)
          }
        }
      }

      console.log('✅ All data loaded')
      setDataFetched(true)

    } catch (err) {
      console.error('Error fetching admin data:', err)
      setError('Failed to load dashboard data.')
      setLoading(false)
      setDataFetched(true)
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  const handleCreateProject = async (e) => {
    e.preventDefault()
    setCreatingProject(true)
    
    try {
      console.log('🚀 Creating/Updating project with data:', newProject)
      console.log('Edit mode:', isEditMode)
      console.log('Project ID:', editingProjectId)
      
      // Validate required fields
      if (!newProject.name.trim()) {
        alert('Project name is required')
        setCreatingProject(false)
        return
      }
      
      if (!newProject.description.trim()) {
        alert('Project description is required')
        setCreatingProject(false)
        return
      }
      
      if (!newProject.client_id) {
        alert('Please select a client')
        setCreatingProject(false)
        return
      }
      
      if (isEditMode && editingProjectId) {
        // UPDATE EXISTING PROJECT
        console.log('📝 Updating existing project:', editingProjectId)
        
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .update({
            name: newProject.name.trim(),
            description: newProject.description.trim(),
            client_id: newProject.client_id,
            budget: parseFloat(newProject.budget) || 0,
            start_date: newProject.start_date || null,
            end_date: newProject.end_date || null,
            priority: newProject.priority
          })
          .eq('id', editingProjectId)
          .select()
          .single()
        
        if (projectError) {
          console.error('❌ Project update error:', projectError)
          throw projectError
        }
        
        console.log('✅ Project updated successfully:', projectData)
        
        // Update project members if employees were selected
        if (newProject.selectedEmployees.length > 0) {
          console.log('👥 Updating employees for project:', newProject.selectedEmployees)
          
          // First, remove existing project members
          const { error: deleteError } = await supabase
            .from('project_members')
            .delete()
            .eq('project_id', editingProjectId)
          
          if (deleteError) {
            console.error('❌ Error deleting existing members:', deleteError)
          } else {
            console.log('✅ Existing project members deleted')
          }
          
          // Then add new project members
          const projectMembers = newProject.selectedEmployees.map(employeeId => ({
            project_id: editingProjectId,
            user_id: employeeId,
            role: 'member'
          }))
          
          console.log('📝 New project members data:', projectMembers)
          
          const { data: membersData, error: membersError } = await supabase
            .from('project_members')
            .insert(projectMembers)
            .select()
          
          if (membersError) {
            console.error('❌ Project members update error:', membersError)
            console.error('❌ Error details:', membersError.message)
            console.error('❌ Error code:', membersError.code)
            // Don't throw error, project is already updated
          } else {
            console.log('✅ Project members updated successfully:', membersData)
          }
        } else {
          console.log('ℹ️ No employees selected for this project update')
        }
        
        alert('Project updated successfully!')
        
      } else {
        // CREATE NEW PROJECT
        console.log('🆕 Creating new project')
        
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .insert({
            name: newProject.name.trim(),
            description: newProject.description.trim(),
            client_id: newProject.client_id,
            budget: parseFloat(newProject.budget) || 0,
            start_date: newProject.start_date || null,
            end_date: newProject.end_date || null,
            priority: newProject.priority,
            status: 'active',
            progress: 0
          })
          .select()
          .single()
        
        if (projectError) {
          console.error('❌ Project creation error:', projectError)
          throw projectError
        }
        
        console.log('✅ Project created successfully:', projectData)
        
        // Add selected employees to project_members table
        if (newProject.selectedEmployees.length > 0) {
          console.log('👥 Adding employees to project:', newProject.selectedEmployees)
          
          const projectMembers = newProject.selectedEmployees.map(employeeId => ({
            project_id: projectData.id,
            user_id: employeeId,
            role: 'member'
          }))
          
          console.log('📝 Project members data to insert:', projectMembers)
          
          const { data: membersData, error: membersError } = await supabase
            .from('project_members')
            .insert(projectMembers)
            .select()
          
          if (membersError) {
            console.error('❌ Project members error:', membersError)
            console.error('❌ Error details:', membersError.message)
            console.error('❌ Error code:', membersError.code)
            // Don't throw error, project is already created
          } else {
            console.log('✅ Project members added successfully:', membersData)
          }
        } else {
          console.log('ℹ️ No employees selected for this project')
        }
        
        alert('Project created successfully!')
      }
      
      // Reset form and close modal
      resetProjectForm()
      
      // Refresh projects data
      setDataFetched(false)
      await fetchAllData()
      
    } catch (error) {
      console.error('❌ Error creating/updating project:', error)
      alert(`Failed to ${isEditMode ? 'update' : 'create'} project: ${error.message}`)
    } finally {
      setCreatingProject(false)
    }
  }

  const resetProjectForm = () => {
    setNewProject({
      name: '',
      description: '',
      client_id: '',
      budget: '',
      start_date: '',
      end_date: '',
      priority: 'medium',
      selectedEmployees: []
    })
    setIsEditMode(false)
    setEditingProjectId(null)
    setShowCreateProject(false)
  }

  const handleEditProject = async (project) => {
    console.log('📝 Editing project:', project)
    
    // Load existing project members
    let existingMembers = []
    try {
      console.log('🔍 Loading existing project members for project:', project.id)
      
      const { data: membersData, error: membersError } = await supabase
        .from('project_members')
        .select('user_id')
        .eq('project_id', project.id)
      
      if (!membersError && membersData) {
        existingMembers = membersData.map(member => member.user_id)
        console.log('👥 Existing project members loaded:', existingMembers)
        console.log('📊 Members data from database:', membersData)
      } else {
        console.error('❌ Error loading existing members:', membersError)
        console.log('📊 Members data:', membersData)
      }
    } catch (error) {
      console.error('❌ Exception loading existing members:', error)
    }
    
    setNewProject({
      name: project.name || '',
      description: project.description || '',
      client_id: project.client_id || '',
      budget: project.budget?.toString() || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      priority: project.priority || 'medium',
      selectedEmployees: existingMembers
    })
    
    setIsEditMode(true)
    setEditingProjectId(project.id)
    setShowCreateProject(true)
  }

  const handleDeleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return
    }

    try {
      console.log('🗑️ Deleting project:', projectId)
      
      // Delete project from database
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
      
      if (error) {
        console.error('❌ Project deletion error:', error)
        throw error
      }
      
      console.log('✅ Project deleted successfully')
      
      // Refresh projects data
      setDataFetched(false)
      await fetchAllData()
      
      alert('Project deleted successfully!')
      
    } catch (error) {
      console.error('❌ Error deleting project:', error)
      alert(`Failed to delete project: ${error.message}`)
    }
  }

  // User Management Functions
  const handleAddUser = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(newUser.email)) {
        throw new Error('Please enter a valid email address')
      }

      // Validate password
      if (newUser.password.length < 6) {
        throw new Error('Password must be at least 6 characters long')
      }

      console.log('🚀 Creating user with email:', newUser.email)
      
      // Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUser.email.trim(),
        password: newUser.password,
        options: {
          data: {
            name: newUser.name,
            role: newUser.role
          }
        }
      })

      if (authError) {
        console.error('❌ Auth signup error:', authError)
        throw authError
      }

      // Create profile in profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: newUser.email,
          name: newUser.name,
          role: newUser.role,
          phone: newUser.phone,
          company: newUser.company,
          address: newUser.address,
          bio: newUser.bio
        })
        .select()
        .single()

      if (profileError) {
        throw profileError
      }

      // Refresh users list
      setDataFetched(false)
      await fetchAllData()
      
      // Reset form and close modal
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'client',
        phone: '',
        company: '',
        address: '',
        bio: ''
      })
      setShowAddUserModal(false)
      
      alert('User created successfully!')
    } catch (error) {
      console.error('Error creating user:', error)
      
      // Provide more specific error messages
      let errorMessage = 'Failed to create user'
      if (error.message?.includes('Email address') && error.message?.includes('invalid')) {
        errorMessage = 'Please enter a valid email address'
      } else if (error.message?.includes('Password')) {
        errorMessage = 'Password must be at least 6 characters long'
      } else if (error.message?.includes('already registered')) {
        errorMessage = 'This email is already registered'
      } else {
        errorMessage = `Failed to create user: ${error.message}`
      }
      
      alert(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete user: ${userEmail}?`)) {
      return
    }

    try {
      // Delete from profiles table first
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId)

      if (profileError) {
        throw profileError
      }

      // Refresh users list
      setDataFetched(false)
      await fetchAllData()
      alert('User deleted successfully!')
    } catch (error) {
      console.error('Error deleting user:', error)
      alert(`Failed to delete user: ${error.message}`)
    }
  }

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      console.log('🔄 Updating user role:', userId, 'to', newRole)
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()

      console.log('📊 Role update result:', { data, error })

      if (error) {
        console.error('❌ Role update error:', error)
        throw error
      }

      console.log('✅ Role update successful:', data)

      // Refresh users list
      setDataFetched(false)
      await fetchAllData()
      alert('User role updated successfully!')
    } catch (error) {
      console.error('❌ Error updating user role:', error)
      alert(`Failed to update user role: ${error.message}`)
    }
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setEditUser({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'client',
      phone: user.phone || '',
      company: user.company || '',
      address: user.address || '',
      bio: user.bio || ''
    })
    setShowEditUserModal(true)
  }

  const handleUpdateUser = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      console.log('🔄 Updating user:', editingUser.id)
      console.log('📝 Update data:', editUser)
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          name: editUser.name,
          email: editUser.email,
          role: editUser.role,
          phone: editUser.phone,
          company: editUser.company,
          address: editUser.address,
          bio: editUser.bio,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingUser.id)
        .select()

      console.log('📊 Update result:', { data, error })

      if (error) {
        console.error('❌ Update error:', error)
        throw error
      }

      console.log('✅ Update successful:', data)

      // Refresh users list
      setDataFetched(false)
      await fetchAllData()
      
      // Close modal
      setShowEditUserModal(false)
      setEditingUser(null)
      
      alert('User updated successfully!')
    } catch (error) {
      console.error('❌ Error updating user:', error)
      alert(`Failed to update user: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Upload Functions
  const handleImageUpload = async (file, isEdit = false) => {
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, GIF, WebP)')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    try {
      setUploadingImage(true)
      
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName)

      // Update the appropriate state
      if (isEdit) {
        setEditProduct({...editProduct, image_url: urlData.publicUrl})
      } else {
        setNewProduct({...newProduct, image_url: urlData.publicUrl})
      }

      alert('Image uploaded successfully!')
    } catch (error) {
      console.error('Error uploading image:', error)
      alert(`Failed to upload image: ${error.message}`)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleHtmlUpload = async (file, isEdit = false) => {
    if (!file) return

    // Validate file type
    if (!file.name.endsWith('.html')) {
      alert('Please select a valid HTML file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB')
      return
    }

    try {
      setUploadingHtml(true)
      
      // Read file content
      const reader = new FileReader()
      reader.onload = async (e) => {
        const htmlContent = e.target.result
        
        // Create a local file URL using Blob
        const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' })
        const localUrl = URL.createObjectURL(blob)
        
        // For now, we'll use a simple approach - store the HTML content as a data URL
        const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(htmlContent)}`
        
        // Update the appropriate state with data URL
        if (isEdit) {
          setEditProduct({...editProduct, html_content: dataUrl})
        } else {
          setNewProduct({...newProduct, html_content: dataUrl})
        }

        alert('HTML file processed successfully!')
        setUploadingHtml(false)
      }
      
      reader.readAsText(file)
    } catch (error) {
      console.error('Error processing HTML:', error)
      alert(`Failed to process HTML file: ${error.message}`)
      setUploadingHtml(false)
    }
  }

  // Product Management Functions
  const handleAddProduct = async (e) => {
    e.preventDefault()
    setSavingProduct(true)
    
    try {
      console.log('🔄 Adding product:', newProduct)

      // Helpers to sanitize pasted content
      const normalizeText = (t) => {
        let str = (t || '').toString()
          .normalize('NFKC')
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/[\u2013\u2014]/g, '-')
          .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ')
          .replace(/[\r\t]+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim()
        // Remove non-printable/control chars
        str = str.replace(/[^\x09\x0A\x0D\x20-\x7E]+/g, '')
        return str.length > 20000 ? str.slice(0, 20000) : str
      }
      const sanitizeUrl = (u) => {
        let s = (u || '').toString().normalize('NFKC')
        // strip zero-width and spaces
        s = s.replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ').trim()
        if (!s) return null
        // collapse inner whitespace into %20 for paths
        s = s.replace(/\s+/g, '%20')
        try {
          const url = new URL(s)
          if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
          // Cap URL length; if too long, drop query/hash
          let out = url.toString()
          if (out.length > 512) {
            out = `${url.origin}${url.pathname}`
          }
          return out
        } catch {
          return null
        }
      }
      const normalizePrice = (p) => {
        const cleaned = (p ?? '').toString().replace(/[^0-9.]/g, '')
        return cleaned ? parseFloat(cleaned) : null
      }
      // Short timeout to avoid infinite loading on network stalls
      const raceTimeout = (p) => Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error('Save timed out (12s)')), 12000))
      ])

      // Build sanitized payload
      const payload = {
        name: normalizeText(newProduct.name).slice(0, 200),
        description: normalizeText(newProduct.description),
        price: normalizePrice(newProduct.price),
        category: (newProduct.category || 'website').toString(),
        is_active: !!newProduct.is_active,
        image_url: sanitizeUrl(newProduct.image_url),
        live_link: sanitizeUrl(newProduct.live_link),
        specifications: normalizeText(newProduct.specifications),
        key_features: normalizeText(newProduct.key_features),
        third_party_link: sanitizeUrl(newProduct.third_party_link),
        third_party_price: normalizePrice(newProduct.third_party_price),
        html_content: normalizeText(newProduct.html_content),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      // Basic required validation
      if (!payload.name) {
        alert('Product name is required')
        setLoading(false)
        return
      }
      if (!payload.description) {
        alert('Product description is required')
        setLoading(false)
        return
      }
      
      // Skip preflight to avoid select/RLS latency; insert will surface real errors quickly

      console.log('📤 Insert start…')
      let data, error
      // Try full payload without artificial timeout to capture real DB error
      const res = await supabase
        .from('products')
        .insert(payload, { returning: 'minimal' })
      data = res.data; error = res.error

      // If failed, retry minimal payload to bypass problematic fields
      if (error) {
        console.warn('⚠️ Insert error, retrying with minimal payload:', error)
        const minimal = {
          name: payload.name,
          description: payload.description,
          price: payload.price,
          category: payload.category,
          is_active: payload.is_active,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        const res2 = await supabase
          .from('products')
          .insert(minimal, { returning: 'minimal' })
        data = res2.data; error = res2.error
      }
      console.log('📥 Insert done')

      console.log('📊 Add product result:', { data, error })

      if (error) {
        console.error('❌ Add product error:', error)
        throw error
      }

      console.log('✅ Product added successfully:', data)

      // Reset form
      setNewProduct({
        name: '',
        description: '',
        price: '',
        category: 'website',
        is_active: true,
        image_url: '',
        live_link: '',
        specifications: '',
        key_features: '',
        third_party_link: '',
        third_party_price: '',
        html_content: ''
      })
      setShowAddProductModal(false)
      
      // Refresh products list
      setDataFetched(false)
      await fetchAllData()
      
      alert('Product added successfully!')
    } catch (error) {
      console.error('❌ Error adding product:', error)
      alert(`Failed to add product: ${error.message}`)
    } finally {
      setSavingProduct(false)
    }
  }

  const handleEditProduct = (product) => {
    setEditingProduct(product)
    setEditProduct({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      category: product.category || 'website',
      is_active: product.is_active,
      image_url: product.image_url || '',
      live_link: product.live_link || '',
      specifications: product.specifications || '',
      key_features: product.key_features || '',
      third_party_link: product.third_party_link || '',
      third_party_price: product.third_party_price?.toString() || '',
      html_content: product.html_content || ''
    })
    setShowEditProductModal(true)
  }

  const handleUpdateProduct = async (e) => {
    e.preventDefault()
    setSavingProduct(true)
    
    try {
      console.log('🔄 Updating product:', editingProduct.id)
      console.log('📝 Update data:', editProduct)
      
      const normalizeText = (t) => {
        let str = (t || '').toString()
          .normalize('NFKC')
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/[\u2013\u2014]/g, '-')
          .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ')
          .replace(/[\r\t]+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim()
        // Remove non-printable/control chars
        str = str.replace(/[^\x09\x0A\x0D\x20-\x7E]+/g, '')
        return str.length > 20000 ? str.slice(0, 20000) : str
      }
      const sanitizeUrl = (u) => {
        let s = (u || '').toString().normalize('NFKC')
        // strip zero-width and spaces
        s = s.replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ').trim()
        if (!s) return null
        // collapse inner whitespace into %20 for paths
        s = s.replace(/\s+/g, '%20')
        try {
          const url = new URL(s)
          if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
          // Cap URL length; if too long, drop query/hash
          let out = url.toString()
          if (out.length > 512) {
            out = `${url.origin}${url.pathname}`
          }
          return out
        } catch {
          return null
        }
      }
      const normalizePrice = (p) => {
        const cleaned = (p ?? '').toString().replace(/[^0-9.]/g, '')
        return cleaned ? parseFloat(cleaned) : null
      }
      const raceTimeout = (p) => Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error('Save timed out (8s)')), 8000))
      ])

      const payload = {
        name: normalizeText(editProduct.name).slice(0, 200),
        description: normalizeText(editProduct.description),
        price: normalizePrice(editProduct.price),
        category: (editProduct.category || 'website').toString(),
        is_active: !!editProduct.is_active,
        image_url: sanitizeUrl(editProduct.image_url),
        live_link: sanitizeUrl(editProduct.live_link),
        specifications: normalizeText(editProduct.specifications),
        key_features: normalizeText(editProduct.key_features),
        third_party_link: sanitizeUrl(editProduct.third_party_link),
        third_party_price: normalizePrice(editProduct.third_party_price),
        html_content: normalizeText(editProduct.html_content),
        updated_at: new Date().toISOString()
      }

      if (!payload.name) {
        alert('Product name is required')
        setLoading(false)
        return
      }
      if (!payload.description) {
        alert('Product description is required')
        setLoading(false)
        return
      }

      // Try full payload without artificial timeout to capture real DB error
      let data, error
      const resU = await supabase
        .from('products')
        .update(payload, { returning: 'minimal' })
        .eq('id', editingProduct.id)
      data = resU.data; error = resU.error

      // If failed, retry with a minimal safe payload
      if (error) {
        console.warn('⚠️ Update error, retrying with minimal payload:', error)
        const minimal = {
          name: payload.name,
          description: payload.description,
          price: payload.price,
          category: payload.category,
          is_active: payload.is_active,
          updated_at: new Date().toISOString()
        }
        const resU2 = await supabase
          .from('products')
          .update(minimal, { returning: 'minimal' })
          .eq('id', editingProduct.id)
        data = resU2.data; error = resU2.error
      }

      console.log('📊 Update product result:', { data, error })

      if (error) {
        console.error('❌ Update product error:', error)
        throw error
      }

      console.log('✅ Product updated successfully:', data)

      // Close modal
      setShowEditProductModal(false)
      setEditingProduct(null)
      
      // Refresh products list
      setDataFetched(false)
      await fetchAllData()
      
      alert('Product updated successfully!')
    } catch (error) {
      console.error('❌ Error updating product:', error)
      alert(`Failed to update product: ${error.message}`)
    } finally {
      setSavingProduct(false)
    }
  }

  const handleDeleteProduct = async (productId, productName) => {
    if (!confirm(`Are you sure you want to delete product: ${productName}?`)) {
      return
    }

    try {
      console.log('🔄 Deleting product:', productId)
      
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId)

      console.log('📊 Delete product result:', { error })

      if (error) {
        console.error('❌ Delete product error:', error)
        throw error
      }

      console.log('✅ Product deleted successfully')

      // Refresh products list
      setDataFetched(false)
      await fetchAllData()
      
      alert('Product deleted successfully!')
    } catch (error) {
      console.error('❌ Error deleting product:', error)
      alert(`Failed to delete product: ${error.message}`)
    }
  }

  // Filter and search users
  const filteredUsers = users.filter(user => {
    const matchesFilter = userFilter === 'all' || user.role === userFilter
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.company?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered':
        return 'bg-green-100 text-green-800'
      case 'open':
        return 'bg-yellow-100 text-yellow-800'
      case 'closed':
        return 'bg-gray-200 text-gray-800'
      case 'completed':
      case 'paid':
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'pending':
      case 'in_progress':
      case 'sent':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
      case 'overdue':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleReplyToTicket = async (e) => {
    e.preventDefault()
    if (!replyingTicket) return
    try {
      const message = (replyMessage || '').toString().trim()
      if (!message) { alert('Please type a reply'); return }
      const { error } = await supabase
        .rpc('st_add_message', { p_ticket_id: replyingTicket.id, p_sender_role: 'admin', p_message: message })
      if (error) throw error
      // Optimistically append the new message to the conversation
      setSupportMessages(prev => [
        ...prev,
        { id: `${Date.now()}`, ticket_id: replyingTicket.id, sender_role: 'admin', message, created_at: new Date().toISOString() }
      ])
      // Also refresh from server in background
      loadMessagesFor(replyingTicket)
      const { data: tickets } = await supabase.rpc('st_list_all_tickets_expanded')
      setSupportTickets(tickets || [])
      setReplyMessage('')
    } catch (err) {
      console.error('Reply failed:', err)
      alert(`Failed to reply: ${err.message}`)
    }
  }

  const getClientName = (clientId) => {
    if (!clientId) return 'Unknown Client'
    const client = clients.find(c => c.id === clientId)
    return client ? client.name : 'Unknown Client'
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment)
    setShowEditAppointmentModal(true)
  }

  const handleSaveAppointment = async (e) => {
    e.preventDefault()
    if (!editingAppointment) return
    try {
      const fd = new FormData(e.currentTarget)
      const payload = {
        name: fd.get('name') || editingAppointment.name,
        email: fd.get('email') || editingAppointment.email,
        phone: fd.get('phone') || editingAppointment.phone,
        service: fd.get('service') || editingAppointment.service,
        date: fd.get('date') || editingAppointment.date,
        time_slot: fd.get('time_slot') || editingAppointment.time_slot,
        status: fd.get('status') || editingAppointment.status,
        message: fd.get('message') || editingAppointment.message,
      }
      const { error } = await supabase
        .from('appointments')
        .update(payload)
        .eq('id', editingAppointment.id)
      if (error) throw error
      setAppointments(prev => prev.map(a => a.id === editingAppointment.id ? { ...a, ...payload } : a))
      setShowEditAppointmentModal(false)
      setEditingAppointment(null)
    } catch (err) {
      console.error('❌ Failed to save appointment:', err)
      alert(`Failed to save: ${err.message}`)
    }
  }

  const handleDeleteAppointment = async (appointment) => {
    if (!confirm('Permanently delete this appointment from the database?')) return
    try {
      setDeletingAppointmentId(appointment.id)
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', appointment.id)
      if (error) throw error
      setAppointments(prev => prev.filter(a => a.id !== appointment.id))
    } catch (err) {
      console.error('❌ Failed to delete appointment:', err)
      alert(`Failed to delete: ${err.message}`)
    } finally {
      setDeletingAppointmentId(null)
    }
  }

  const generateMeetLinkPlaceholder = () => {
    // Temporary placeholder link; replace with Calendar API integration if needed
    // Note: meet.google.com/new will generate a fresh room on click
    return 'https://meet.google.com/new'
  }

  const handleConfirmAppointment = async (appointment) => {
    try {
      const meetLink = appointment.meet_link || generateMeetLinkPlaceholder()
      const { error } = await supabase
        .from('appointments')
        .update({ status: 'confirmed', meet_link: meetLink })
        .eq('id', appointment.id)
      if (error) throw error

      const updated = { ...appointment, status: 'confirmed', meet_link: meetLink }
      setAppointments(prev => prev.map(a => a.id === appointment.id ? updated : a))

      // Send confirmation email to client
      await EmailService.sendAppointmentConfirmation({
        name: updated.name,
        email: updated.email,
        service: updated.service,
        date: updated.date,
        time_slot: updated.time_slot,
        meet_link: updated.meet_link,
      })
    } catch (err) {
      console.error('❌ Failed to confirm appointment:', err)
      alert(`Failed to confirm: ${err.message}`)
    }
  }

  const handleCreateAppointmentSubmit = async (e) => {
    e.preventDefault()
    try {
      setCreatingAppointment(true)
      const normalize = (t) => (t || '').toString().trim()
      const payload = {
        name: normalize(newAppointment.name),
        email: normalize(newAppointment.email),
        phone: normalize(newAppointment.phone),
        service: normalize(newAppointment.service),
        date: normalize(newAppointment.date),
        time_slot: normalize(newAppointment.time_slot),
        message: normalize(newAppointment.message),
        status: newAppointment.status || 'pending'
      }
      if (!payload.name || !payload.email || !payload.phone || !payload.service || !payload.date || !payload.time_slot) {
        alert('Please fill name, email, phone, service, date and time slot')
        setCreatingAppointment(false)
        return
      }
      const { error } = await supabase
        .from('appointments')
        .insert(payload, { returning: 'minimal' })
      if (error) throw error
      // refresh list
      const { data: appts } = await supabase
        .from('appointments')
        .select('*')
        .in('status', ['pending','confirmed'])
        .order('date', { ascending: true })
        .order('time_slot', { ascending: true })
      setAppointments(appts || [])
      // reset and close
      setNewAppointment({ name: '', email: '', phone: '', service: '', date: '', time_slot: '', message: '', status: 'pending' })
      setShowCreateAppointmentModal(false)
      alert('Appointment created')
    } catch (err) {
      console.error('Create appointment failed:', err)
      alert(`Failed to create appointment: ${err.message}`)
    } finally {
      setCreatingAppointment(false)
    }
  }

  const getProjectMembers = (projectId) => {
    console.log('🔍 Getting project members for project:', projectId)
    console.log('📊 All project members:', projectMembers)
    console.log('📊 Project members length:', projectMembers.length)
    
    const members = projectMembers.filter(member => member.project_id === projectId)
    console.log('👥 Members for this project:', members)
    console.log('👥 Members count for this project:', members.length)
    
    const formattedMembers = members.map(member => {
      const formatted = {
        id: member.user_id,
        name: member.user?.name || 'Unknown',
        email: member.user?.email || '',
        role: member.role
      }
      console.log('👤 Formatted member:', formatted)
      return formatted
    })
    
    console.log('✅ Returning formatted members:', formattedMembers)
    return formattedMembers
  }

  // Test function to manually check project members
  const testProjectMembers = async () => {
    console.log('🧪 Testing project members manually...')
    
    try {
      const { data: testData, error: testError } = await supabase
        .from('project_members')
        .select('*')
      
      if (!testError && testData) {
        console.log('✅ Manual test successful:', testData)
        console.log('📊 Test data length:', testData.length)
        
        // Fetch user details separately
        const userIds = testData.map(member => member.user_id)
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('id, name, email, role')
          .in('id', userIds)
        
        if (!usersError && usersData) {
          console.log('✅ User details fetched:', usersData.length)
          
          // Combine project members with user details
          const combinedData = testData.map(member => ({
            ...member,
            user: usersData.find(user => user.id === member.user_id)
          }))
          
          console.log('✅ Combined test data:', combinedData)
          setProjectMembers(combinedData)
        } else {
          console.error('❌ Error fetching user details:', usersError)
          setProjectMembers(testData)
        }
      } else {
        console.error('❌ Manual test failed:', testError)
      }
    } catch (error) {
      console.error('❌ Manual test error:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Database Setup Required</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-yellow-800 mb-2">Quick Fix:</h3>
            <ol className="text-sm text-yellow-700 text-left space-y-1">
              <li>1. Run SQL script from SIMPLE_DATABASE_SETUP.md</li>
              <li>2. Refresh this page</li>
            </ol>
          </div>
          <button 
            onClick={fetchAllData}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gradient-to-br from-gray-800 to-gray-900 text-white shadow-lg p-6 flex flex-col">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-extrabold text-white">Admin Panel</h2>
            <p className="text-sm text-gray-300 mt-1">Welcome, {profile?.name || user?.email}</p>
          </div>
          
          <nav className="flex-1 space-y-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
              </svg>
              Overview
            </button>
            
            <button
              onClick={() => setActiveTab('users')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'users' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              Users ({users.length})
            </button>
            
            <button
              onClick={() => setActiveTab('products')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'products' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Products ({products.length})
            </button>
            
            <button
              onClick={() => setActiveTab('orders')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'orders' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Orders ({orders.length})
            </button>
            
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'projects' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Projects ({projects.length})
            </button>
            
            <button
              onClick={() => setActiveTab('invoices')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'invoices' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Invoices ({invoices.length})
            </button>
            
            <button
              onClick={() => setActiveTab('services')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'services' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M4 6h16M4 10h16" />
              </svg>
              Services
            </button>
            
            <button
              onClick={() => setActiveTab('appointments')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'appointments' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Appointments ({appointments.length})
            </button>

            <button
              onClick={() => setActiveTab('support')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'support' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.88 9h.01M14.12 9h.01M8 13a4 4 0 008 0" />
              </svg>
              <span className="flex items-center">
                Client Support
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-500 text-white">
                  {(supportTickets || []).filter(t => (t.status || 'open') === 'open').length}
                </span>
              </span>
            </button>

            <button
              onClick={() => setActiveTab('livechat')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'livechat' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h8m-6 8l-4-4V6a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H9z" />
              </svg>
              <span className="flex items-center">
                Live Chat
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-500 text-white">
                  {publicChats.filter(c => (c.status || 'open') !== 'closed').length}
                </span>
              </span>
            </button>

            <button
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'settings' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-1.14 1.97-1.14 2.269 0a1.724 1.724 0 002.573 1.066c.99-.57 2.21.65 1.64 1.64a1.724 1.724 0 001.066 2.573c1.14.3 1.14 1.97 0 2.269a1.724 1.724 0 00-1.066 2.573c.57.99-.65 2.21-1.64 1.64a1.724 1.724 0 00-2.573 1.066c-.3 1.14-1.97 1.14-2.269 0a1.724 1.724 0 00-2.573-1.066c-.99.57-2.21-.65-1.64-1.64a1.724 1.724 0 00-1.066-2.573c-1.14-.3-1.14-1.97 0-2.269.6-.158 1.08-.54 1.066-1.066-.57-.99.65-2.21 1.64-1.64.72.416 1.628.094 2.573-1.066z" />
              </svg>
              Settings
            </button>

            <button
              onClick={() => setActiveTab('contacts')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'contacts' 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h8m-6 8l-4-4V6a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H9z" />
              </svg>
              Contact Messages
            </button>
          </nav>
          
          <div className="mt-8">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors text-red-300 hover:bg-gray-700 hover:text-red-100"
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 overflow-y-auto">
          {activeTab === 'overview' && (
            <div>
              <h1 className="text-4xl font-extrabold text-gray-900 mb-8">Admin Dashboard</h1>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Total Users</h3>
                      <p className="text-3xl font-bold text-blue-600">{users.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Total Products</h3>
                      <p className="text-3xl font-bold text-green-600">{products.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Total Orders</h3>
                      <p className="text-3xl font-bold text-yellow-600">{orders.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Active Projects</h3>
                      <p className="text-3xl font-bold text-purple-600">{projects.filter(p => p.status === 'active' || p.status === 'in_progress').length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Orders</h3>
                  {orders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-900">No orders yet</h3>
                      <p className="mt-1 text-sm text-gray-500">Orders will appear here when customers place them.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {orders.slice(0, 5).map(order => (
                        <li key={order.id} className="py-3 flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">Order #{order.id.slice(0, 8)}</p>
                            <p className="text-sm text-gray-500">{order.client_id || 'Unknown Client'}</p>
                          </div>
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)} mr-3`}>
                              {order.status || 'pending'}
                            </span>
                            <span className="text-sm font-medium text-gray-900">৳{order.total_amount?.toLocaleString() || '0'}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Projects</h3>
                  {projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <h3 className="mt-2 text-lg font-medium text-gray-900">No projects yet</h3>
                      <p className="mt-1 text-sm text-gray-500">Projects will appear here when created.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {projects.slice(0, 5).map(project => (
                        <li key={project.id} className="py-3">
                          <div className="flex justify-between items-center">
                            <p className="font-medium text-gray-900">{project.name || 'Unnamed Project'}</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                              {project.status || 'pending'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{project.client_id || 'Unknown Client'}</p>
                          <div className="mt-2">
                            <ProjectProgress projectId={project.id} fallbackPercent={project.progress || 0} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

    {showCreateAppointmentModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Schedule Appointment</h2>
              <button onClick={() => setShowCreateAppointmentModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleCreateAppointmentSubmit} noValidate className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input type="text" required value={newAppointment.name} onChange={(e)=>setNewAppointment({...newAppointment,name:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={newAppointment.email} onChange={(e)=>setNewAppointment({...newAppointment,email:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="tel" required value={newAppointment.phone} onChange={(e)=>setNewAppointment({...newAppointment,phone:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service *</label>
                  <input type="text" required list="services-list" value={newAppointment.service} onChange={(e)=>setNewAppointment({...newAppointment,service:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  <datalist id="services-list">
                    {servicesList.map(s => (<option key={s.id} value={s.name} />))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" required value={newAppointment.date} onChange={(e)=>setNewAppointment({...newAppointment,date:e.target.value, time_slot:''})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot *</label>
                  {newAppointment.date ? (
                    availableAdminSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableAdminSlots.map((slot)=> (
                          <button type="button" key={slot} onClick={()=>setNewAppointment({...newAppointment, time_slot: slot})} className={`p-2 text-sm rounded-lg border ${newAppointment.time_slot===slot ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:border-primary-500'}`}>{slot}</button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No available slots for this date</p>
                    )
                  ) : (
                    <input type="text" required placeholder="Select date first" value={newAppointment.time_slot} onChange={(e)=>setNewAppointment({...newAppointment,time_slot:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea rows={3} value={newAppointment.message} onChange={(e)=>setNewAppointment({...newAppointment,message:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <button type="button" onClick={()=>setShowCreateAppointmentModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={creatingAppointment} className="btn-primary">{creatingAppointment ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-4xl font-extrabold text-gray-900">User Management</h1>
                  <p className="text-gray-600 mt-2">Total Users: {users.length} | Showing: {filteredUsers.length}</p>
                </div>
                <div className="flex space-x-3">
                  <button 
                    onClick={() => setShowAddUserModal(true)}
                    className="btn-primary flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add New User
                  </button>
                </div>
              </div>

              {/* Filter and Search */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
                    <select
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Users ({users.length})</option>
                      <option value="admin">Admins ({users.filter(u => u.role === 'admin').length})</option>
                      <option value="employee">Employees ({users.filter(u => u.role === 'employee').length})</option>
                      <option value="client">Clients ({users.filter(u => u.role === 'client').length})</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
                    <input
                      type="text"
                      placeholder="Search by name, email, or company..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <div className="text-gray-500">
                              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                              </svg>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                              <p className="text-sm text-gray-500">
                                {searchTerm || userFilter !== 'all' 
                                  ? 'Try adjusting your search or filter criteria.' 
                                  : 'No users have been created yet.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map(user => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-gray-600">
                                    {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{user.name || 'No Name'}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={user.role || 'client'}
                                onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                className={`px-2 py-1 rounded-full text-xs font-semibold border-0 ${
                                  user.role === 'admin' ? 'bg-red-100 text-red-800' :
                                  user.role === 'employee' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                              >
                                <option value="client">Client</option>
                                <option value="employee">Employee</option>
                                <option value="admin">Admin</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.company || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(user.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-3">
                                <button 
                                  onClick={() => handleEditUser(user)}
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-4xl font-extrabold text-gray-900">Product Management</h1>
                  <p className="text-gray-600 mt-2">Total Products: {products.length}</p>
                </div>
                <button 
                  onClick={() => setShowAddProductModal(true)}
                  className="btn-primary flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Product
                </button>
              </div>
              
              {products.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">No products yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding your first product.</p>
                  <div className="mt-6">
                    <button 
                      onClick={() => setShowAddProductModal(true)}
                      className="btn-primary"
                    >
                      Add Product
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map(product => (
                    <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Product Image */}
                      {product.image_url && (
                        <div className="h-48 bg-gray-100 overflow-hidden">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.nextSibling.style.display = 'flex'
                            }}
                          />
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500" style={{display: 'none'}}>
                            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-2 text-sm line-clamp-2">{product.description}</p>
                        <p className="text-gray-500 mb-2 text-xs capitalize">Category: {product.category}</p>
                        
                        {/* Key Features Preview */}
                        {product.key_features && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Key Features:</p>
                            <p className="text-xs text-gray-600 line-clamp-2">{product.key_features}</p>
                          </div>
                        )}
                        
                        {/* Live Link */}
                        {product.live_link && (
                          <div className="mb-3">
                            <a 
                              href={product.live_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Live Demo
                            </a>
                          </div>
                        )}
                        
                        {/* Pricing */}
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <span className="text-xl font-bold text-primary-600">৳{product.price?.toLocaleString()}</span>
                            {product.third_party_price && (
                              <span className="text-sm text-gray-500 ml-2">
                                or ৳{product.third_party_price?.toLocaleString()} (3rd party)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleEditProduct(product)}
                            className="btn-secondary text-sm flex-1"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product.id, product.name)}
                            className="btn-danger text-sm flex-1"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Order Management</h1>
                <button className="btn-primary">Export Orders</button>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map(order => (
                        <tr key={order.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{order.id.slice(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.client_id || 'Unknown Client'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ৳{order.total_amount?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-primary-600 hover:text-primary-900 mr-3">View</button>
                            <button className="text-green-600 hover:text-green-900">Process</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Project Management</h1>
                <div className="flex space-x-3">
                  <button 
                    onClick={() => {
                      setIsEditMode(false)
                      setEditingProjectId(null)
                      setShowCreateProject(true)
                    }}
                    className="btn-primary"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create New Project
                  </button>
                </div>
              </div>
              
              {projects.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">No projects yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating your first project.</p>
                  <div className="mt-6">
                    <button 
                      onClick={() => {
                        setIsEditMode(false)
                        setEditingProjectId(null)
                        setShowCreateProject(true)
                      }}
                      className="btn-primary"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create New Project
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {projects.map(project => (
                    <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                              {project.status}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(project.priority)}`}>
                              {project.priority || 'medium'}
                            </span>
                          </div>
                          {/* Team Members Section */}
                          <div className="mt-3">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                              </svg>
                              <span className="text-sm text-gray-600">Team Members:</span>
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {(() => {
                                const members = getProjectMembers(project.id)
                                console.log(`🎯 Project ${project.name} (${project.id}) members:`, members)
                                
                                if (members.length > 0) {
                                  return members.map(member => (
                                    <span key={member.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                      {member.name}
                                    </span>
                                  ))
                                } else {
                                  console.log(`⚠️ No members found for project ${project.name} (${project.id})`)
                                  return <span className="text-xs text-gray-500">No members assigned</span>
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={async () => {
                              const newStatus = project.status === 'active' ? 'inactive' : 'active'
                              try {
                                const { data, error } = await supabase
                                  .from('projects')
                                  .update({ status: newStatus })
                                  .eq('id', project.id)
                                  .select()
                                  .single()
                                if (!error && data) {
                                  setProjects(prev => prev.map(p => p.id === project.id ? { ...p, status: data.status } : p))
                                }
                              } catch (e) {
                                console.error('Failed to toggle status', e)
                              }
                            }}
                            className={`px-3 py-1 rounded text-sm ${project.status === 'active' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}
                          >
                            {project.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{project.description}</p>
                      <div className="mb-4">
                        <ProjectProgress projectId={project.id} fallbackPercent={project.progress || 0} />
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-500">Client: {getClientName(project.client_id)}</p>
                          <p className="text-sm text-gray-500">Budget: ৳{project.budget?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="flex space-x-2 items-center">
                          <button 
                            onClick={() => handleEditProject(project)}
                            className="btn-secondary text-sm"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteProject(project.id)}
                            className="btn-danger text-sm"
                          >
                            Delete
                          </button>
                          <Link to={`/project/${project.id}`} className="btn-primary text-sm">
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Invoice Management</h1>
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-lg border overflow-hidden">
                    <button className={`px-3 py-2 text-sm ${invoiceView==='project'?'bg-primary-600 text-white':'bg-white text-gray-700'}`} onClick={() => setInvoiceView('project')}>Project Invoices</button>
                    <button className={`px-3 py-2 text-sm ${invoiceView==='manual'?'bg-primary-600 text-white':'bg-white text-gray-700'}`} onClick={() => setInvoiceView('manual')}>Manual Invoices</button>
                  </div>
                  {invoiceView==='project' ? (
                    <button onClick={() => setShowInvoiceModal(true)} className="btn-primary">Generate Invoice</button>
                  ) : (
                    <button onClick={() => setShowManualInvoiceModal(true)} className="btn-primary">New Manual Invoice</button>
                  )}
                </div>
              </div>
              
              {invoiceView==='project' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{invoice.id.slice(0, 8)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.client_name || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.project_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ৳{invoice.amount?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.due_date
                              ? new Date(invoice.due_date).toLocaleDateString()
                              : (invoice.created_at
                                  ? new Date(new Date(invoice.created_at).getTime() + 7*24*60*60*1000).toLocaleDateString()
                                  : 'N/A')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-3">
                            <button onClick={() => handleViewInvoice(invoice)} className="text-primary-600 hover:text-primary-900">View</button>
                            <button onClick={() => handleDownloadInvoice(invoice)} className="text-green-600 hover:text-green-900">Download</button>
                            {invoice.status !== 'paid' && (
                              <button onClick={() => handleMarkInvoicePaid(invoice)} className="text-blue-600 hover:text-blue-900">Mark Paid</button>
                            )}
                            <button onClick={() => handleDeleteInvoice(invoice)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {invoiceView==='manual' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product/Topic</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {manualInvoices.map(mi => (
                        <tr key={mi.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{mi.id.slice(0,8)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mi.client_name || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mi.project_name || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mi.subject || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">৳{Number(mi.amount||0).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">{mi.status || 'sent'}</span></td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mi.due_date ? new Date(mi.due_date).toLocaleDateString() : '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex gap-3">
                            <button onClick={() => handleViewManualInvoice(mi)} className="text-primary-600 hover:text-primary-900">View</button>
                            {mi.status !== 'paid' && (
                              <button onClick={() => handleMarkManualInvoicePaid(mi)} className="text-green-600 hover:text-green-800">Make Paid</button>
                            )}
                            <button onClick={() => handleDeleteManualInvoice(mi)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Service Management</h1>
                <button onClick={() => { setEditingService(null); setServiceForm({ name: '', description: '', icon: '' }); setShowServiceModal(true) }} className="btn-primary">Add Service</button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Icon</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {servicesList.map(svc => (
                        <tr key={svc.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{svc.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <span title={svc.description || ''}>
                              {(() => {
                                const d = (svc.description || '').toString().trim()
                                const words = d.split(/\s+/)
                                if (words.length <= 5) return d
                                return words.slice(0, 5).join(' ') + '…'
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{svc.icon || '🧩'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                            <button onClick={() => startEditService(svc)} className="text-primary-600 hover:text-primary-900">Edit</button>
                            <button onClick={() => deleteService(svc)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Settings</h1>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-2xl">
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  console.log('🛠️ Settings: submit clicked')
                  const fd = new FormData(e.currentTarget)
                  const website_name = fd.get('website_name')
                  const company_name = fd.get('company_name')
                  const address = fd.get('address')
                  const phone = fd.get('phone')
                  const email = fd.get('email')
                  const logo_url = fd.get('logo_url')
                  const footer_about = fd.get('footer_about')
                  const facebook_url = fd.get('facebook_url')
                  const linkedin_url = fd.get('linkedin_url')
                  const twitter_url = fd.get('twitter_url')
                  const instagram_url = fd.get('instagram_url')
                  const youtube_url = fd.get('youtube_url')
                  const github_url = fd.get('github_url')
                  const whatsapp_url = fd.get('whatsapp_url')
                  try {
                    setSavingSettings(true)
                    console.log('📦 Settings payload:', { website_name, company_name, address, phone, email, logo_url, footer_about, facebook_url, linkedin_url, twitter_url, instagram_url, youtube_url, github_url, whatsapp_url })
                    // Admin only
                    if (profile?.role !== 'admin') {
                      alert('Only admins can save settings')
                      setSavingSettings(false)
                      return
                    }

                    // Preflight: table/policy reachable
                    const { error: preErr } = await supabase
                      .from('company_settings')
                      .select('id', { count: 'exact', head: true })
                    if (preErr) {
                      console.error('Settings preflight failed:', preErr)
                      alert(`Settings table not accessible: ${preErr.message || 'RLS or table missing'}`)
                      setSavingSettings(false)
                      return
                    }

                    // Upsert single-row settings (id = 'global')
                    const { error } = await supabase
                      .from('company_settings')
                      .upsert({ id: 'global', website_name, company_name, address, phone, email, logo_url, footer_about, facebook_url, linkedin_url, twitter_url, instagram_url, youtube_url, github_url, whatsapp_url }, { onConflict: 'id' })
                    if (error) {
                      console.error('Settings save error:', error)
                      alert(`Failed to save settings: ${error.message}`)
                      setSavingSettings(false)
                      return
                    }
                    alert('Settings saved')
                  } catch (err) {
                    console.error('Settings save failed:', err)
                    alert('Failed to save settings')
                  } finally {
                    setSavingSettings(false)
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website Name</label>
                    <input name="website_name" defaultValue={settingsData?.website_name || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="One Matrix" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <input name="company_name" defaultValue={settingsData?.company_name || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="ONE MATRIX" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea name="address" defaultValue={settingsData?.address || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" rows={3} placeholder="Street, City, Country" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input name="phone" defaultValue={settingsData?.phone || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="+8801XXXXXXXXX" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input name="email" type="email" defaultValue={settingsData?.email || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="info@onematrix.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                    <input name="logo_url" defaultValue={settingsData?.logo_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://.../logo.png" />
                    <div className="mt-3 flex items-center gap-3">
                      <input type="file" accept="image/*" className="text-sm" onChange={async (e) => {
                        const file = e.target.files && e.target.files[0]
                        if (!file) return
                        try {
                          const bucket = 'company-assets'
                          const fileName = `logo_${Date.now()}_${file.name}`
                          const path = `company/${fileName}`
                          const { error: upErr } = await supabase
                            .storage
                            .from(bucket)
                            .upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' })
                          if (upErr) {
                            console.error('Logo upload error:', upErr)
                            alert(upErr.message?.includes('Not Found') ? 'Bucket not found. Please create a public bucket named "company-assets" in Supabase Storage.' : `Upload failed: ${upErr.message}`)
                            return
                          }
                          const { data: pub } = supabase
                            .storage
                            .from(bucket)
                            .getPublicUrl(path)
                          const publicUrl = pub?.publicUrl
                          if (publicUrl) {
                            const formEl = e.target && e.target.form ? e.target.form : null
                            const input = formEl ? formEl.querySelector('input[name="logo_url"]') : null
                            if (input) input.value = publicUrl
                            // keep state in sync so user sees it without reload
                            if (typeof setSettingsData === 'function') {
                              setSettingsData(prev => ({ ...(prev || {}), logo_url: publicUrl }))
                            }
                            alert('Logo uploaded. URL filled automatically.')
                          } else {
                            alert('Uploaded, but failed to get public URL. Make the bucket public or copy the path.')
                          }
                        } catch (err) {
                          console.error('Logo upload failed:', err)
                          alert('Logo upload failed')
                        }
                      }} />
                      <span className="text-xs text-gray-500">Upload to auto-fill URL</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Footer About</label>
                    <textarea name="footer_about" defaultValue={settingsData?.footer_about || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" rows={3} placeholder="We provide comprehensive IT solutions..." />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
                      <input name="facebook_url" defaultValue={settingsData?.facebook_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://facebook.com/yourpage" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                      <input name="linkedin_url" defaultValue={settingsData?.linkedin_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://linkedin.com/company/yourcompany" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Twitter URL</label>
                      <input name="twitter_url" defaultValue={settingsData?.twitter_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://twitter.com/yourhandle" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
                      <input name="instagram_url" defaultValue={settingsData?.instagram_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://instagram.com/yourhandle" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                      <input name="youtube_url" defaultValue={settingsData?.youtube_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://youtube.com/@yourchannel" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">GitHub URL</label>
                      <input name="github_url" defaultValue={settingsData?.github_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://github.com/yourorg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp URL</label>
                    <input name="whatsapp_url" defaultValue={settingsData?.whatsapp_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="https://wa.me/8801XXXXXXXXX" />
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" disabled={savingSettings} className={`btn-primary ${savingSettings ? 'opacity-70 cursor-not-allowed' : ''}`}>{savingSettings ? 'Saving…' : 'Save Settings'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Contact Messages</h1>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contactMessages.map(m => (
                        <tr key={m.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{m.name || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{m.email || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{m.phone || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${m.status === 'replied' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {m.status || 'new'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                            <button onClick={() => handleViewContact(m)} className="text-primary-600 hover:text-primary-900">View</button>
                            {m.status !== 'replied' && (
                              <button onClick={() => handleMarkReplied(m)} className="text-green-600 hover:text-green-900">Mark Replied</button>
                            )}
                            <button onClick={() => handleDeleteContact(m)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                      {contactMessages.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-6 py-6 text-center text-sm text-gray-500">No messages yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {showContactModal && selectedContact && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Message from {selectedContact.name || '—'}</h2>
                  <button onClick={() => { setShowContactModal(false); setSelectedContact(null) }} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><span className="font-medium">Email:</span> {selectedContact.email || '—'}</p>
                  <p><span className="font-medium">Phone:</span> {selectedContact.phone || '—'}</p>
                  <p><span className="font-medium">Subject/Budget:</span> {selectedContact.subject || '—'}</p>
                  <p><span className="font-medium">Service ID:</span> {selectedContact.service_id || '—'}</p>
                  <p><span className="font-medium">Date:</span> {selectedContact.created_at ? new Date(selectedContact.created_at).toLocaleString() : '—'}</p>
                  <div className="mt-3">
                    <p className="font-medium mb-1">Message</p>
                    <div className="p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap text-gray-800">{selectedContact.message || '—'}</div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  {selectedContact.status !== 'replied' && (
                    <button onClick={() => handleMarkReplied(selectedContact)} className="btn-primary">Mark Replied</button>
                  )}
                  <button onClick={() => { setShowContactModal(false); setSelectedContact(null) }} className="btn-secondary">Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900">Appointment Management</h1>
                <button className="btn-primary" onClick={() => setShowCreateAppointmentModal(true)}>Schedule Appointment</button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {appointments.map(appointment => (
                  <div key={appointment.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{appointment.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600"><strong>Email:</strong> {appointment.email}</p>
                      <p className="text-sm text-gray-600"><strong>Phone:</strong> {appointment.phone}</p>
                      <p className="text-sm text-gray-600"><strong>Service:</strong> {appointment.service}</p>
                      <p className="text-sm text-gray-600"><strong>Date:</strong> {new Date(appointment.date).toLocaleDateString()}</p>
                      <p className="text-sm text-gray-600"><strong>Time:</strong> {appointment.time_slot}</p>
                      {appointment.meet_link && (
                        <p className="text-sm text-blue-600">
                          <a href={appointment.meet_link} target="_blank" rel="noreferrer" className="underline">Join Google Meet</a>
                        </p>
                      )}
                    </div>
                    <div className="mt-4 flex space-x-2">
                      <button className="btn-secondary text-sm" onClick={() => handleEditAppointment(appointment)}>Edit</button>
                      {appointment.status === 'confirmed' ? (
                        <button className="btn-primary text-sm opacity-60 cursor-not-allowed" disabled>Confirmed</button>
                      ) : (
                        <button className="btn-primary text-sm" onClick={() => handleConfirmAppointment(appointment)}>Confirm</button>
                      )}
                      <button
                        className={`btn-danger text-sm ${deletingAppointmentId === appointment.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                        disabled={deletingAppointmentId === appointment.id}
                        onClick={() => handleDeleteAppointment(appointment)}
                      >
                        {deletingAppointmentId === appointment.id ? 'Removing…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-4xl font-extrabold text-gray-900">Client Support</h1>
                  <p className="text-gray-600 mt-2">Manage support tickets submitted by clients</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {supportTickets.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-500">No tickets yet.</td>
                        </tr>
                      ) : (
                        supportTickets.map(t => (
                          <tr key={t.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.subject || '(no subject)'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{t.client_email || t.client_id || 'Unknown'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(t.status || 'pending')}`}>{t.status || 'pending'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                              <button className="text-primary-600 hover:text-primary-900" onClick={()=>{ setReplyingTicket(t); setReplyMessage('') }}>Reply</button>
                              <button className="text-red-600 hover:text-red-900 ml-2" onClick={async ()=>{
                                if (!window.confirm('Delete this ticket permanently?')) return
                                try {
                                  const { error } = await supabase.rpc('st_delete_ticket', { p_ticket_id: t.id })
                                  if (error) throw error
                                  const { data: tickets } = await supabase.rpc('st_list_all_tickets_expanded')
                                  setSupportTickets(tickets || [])
                                } catch (e) {
                                  alert(`Delete failed: ${e.message || e}`)
                                }
                              }}>Delete</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {replyingTicket && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Reply to Ticket</h2>
                      <button onClick={()=>{ setReplyingTicket(null); setReplyMessage('') }} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-700"><strong>Subject:</strong> {replyingTicket.subject || '(no subject)'}</p>
                      <p className="text-sm text-gray-700"><strong>Client:</strong> {replyingTicket.client_name || replyingTicket.client_email || replyingTicket.client_id || 'Unknown'}</p>
                    </div>
                    {replyingTicket?.message && (
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded">
                        <p className="text-xs text-gray-500 mb-1">Original message</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{replyingTicket.message}</p>
                      </div>
                    )}
                    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded max-h-56 overflow-y-auto">
                      <p className="text-xs text-gray-500 mb-2">Conversation</p>
                      {supportMessages.length === 0 ? (
                        <p className="text-sm text-gray-500">No messages yet.</p>
                      ) : (
                        supportMessages.map(m => (
                          <div key={m.id} className={`mb-2 p-2 rounded border ${m.sender_role==='admin'?'bg-blue-50 border-blue-200':'bg-gray-50 border-gray-200'}`}>
                            <p className="text-xs text-gray-500 mb-1"><strong>{m.sender_role==='admin'?'Admin':'Client'}</strong> • {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{m.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <form onSubmit={handleReplyToTicket} className="space-y-3">
                      <textarea rows={4} value={replyMessage} onChange={(e)=>setReplyMessage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Type your reply..." />
                      <div className="flex justify-end space-x-2">
                        <button type="button" onClick={()=>{ setReplyingTicket(null); setReplyMessage('') }} className="btn-secondary">Cancel</button>
                        <button type="submit" className="btn-primary">Send Reply</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'livechat' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-4xl font-extrabold text-gray-900">Public Live Chat</h1>
                  <p className="text-gray-600 mt-2">View visitor chats and reply as admin</p>
                </div>
                <div>
                  <button type="button" onClick={refreshLiveChat} className="btn-secondary">Refresh</button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Chats list */}
                <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700">All Chats</h3>
                    <span className="text-xs text-gray-500">{publicChats.length} total</span>
                  </div>
                  <div className="max-h-[28rem] overflow-y-auto divide-y divide-gray-100">
                    {publicChats.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500">No chats yet.</div>
                    ) : (
                      publicChats.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => { setSelectedPublicChat(c); loadPublicChatMessages(c.id) }}
                          className={`w-full text-left p-4 hover:bg-gray-50 ${selectedPublicChat?.id === c.id ? 'bg-gray-50' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0">
                              <div className="h-9 w-9 rounded-full bg-primary-600 text-white grid place-items-center mr-3 flex-shrink-0">
                                <span className="text-sm font-semibold">{getInitial(c.visitor_name || c.visitor_email)}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">{c.visitor_name}</p>
                                <p className="text-xs text-gray-500 truncate">{c.visitor_email}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              c.status === 'closed' ? 'bg-gray-100 text-gray-700' : c.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {c.status}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{new Date(c.updated_at || c.created_at).toLocaleString()}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Thread */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-[70vh]">
                  {!selectedPublicChat ? (
                    <div className="p-8 text-center text-gray-500">Select a chat to view messages</div>
                  ) : (
                    <>
                      <div className="px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0">
                            <div className="h-9 w-9 rounded-full bg-primary-600 text-white grid place-items-center mr-3 flex-shrink-0">
                              <span className="text-sm font-semibold">{getInitial(selectedPublicChat.visitor_name || selectedPublicChat.visitor_email)}</span>
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate">{selectedPublicChat.visitor_name}</h3>
                              <p className="text-xs text-gray-500 truncate">{selectedPublicChat.visitor_email} {selectedPublicChat.visitor_phone ? `• ${selectedPublicChat.visitor_phone}` : ''}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            selectedPublicChat.status === 'closed' ? 'bg-gray-100 text-gray-700' : selectedPublicChat.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {selectedPublicChat.status}
                          </span>
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-gray-50 to-white">
                        {publicChatLoading ? (
                          <div className="text-center text-sm text-gray-500">Loading…</div>
                        ) : publicChatMessages.length === 0 ? (
                          <div className="text-center text-sm text-gray-500">No messages yet.</div>
                        ) : (
                          publicChatMessages.map(m => {
                            const isImage = /^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)$/i.test(m.message || '')
                            const isLink = /^https?:\/\//i.test(m.message || '')
                            return (
                              <div key={m.id} className={`flex ${m.sender==='admin' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`flex ${m.sender==='admin' ? 'flex-row-reverse' : 'flex-row'} max-w-[85%]`}>
                                  <div className={`h-8 w-8 rounded-full ${m.sender==='admin' ? 'bg-blue-600' : 'bg-gray-400'} text-white grid place-items-center ${m.sender==='admin' ? 'ml-3' : 'mr-3'} flex-shrink-0`}>
                                    <span className="text-xs font-semibold">{m.sender==='admin' ? 'A' : getInitial(selectedPublicChat.visitor_name || selectedPublicChat.visitor_email)}</span>
                                  </div>
                                  <div className={`${m.sender==='admin' ? 'items-end' : 'items-start'} flex flex-col`}>
                                    <div className={`px-3 py-2 rounded-2xl shadow-sm border ${m.sender==='admin'?'bg-blue-600 text-white border-blue-600 rounded-br-sm':'bg-white text-gray-900 border-gray-200 rounded-bl-sm'}`}>
                                      {isImage ? (
                                        <a href={m.message} target="_blank" rel="noreferrer">
                                          <img src={m.message} alt="attachment" className="max-h-56 rounded-lg" />
                                        </a>
                                      ) : isLink ? (
                                        <a href={m.message} target="_blank" rel="noreferrer" className={`${m.sender==='admin'?'text-white underline':'text-blue-600 underline'} break-all`}>{m.message}</a>
                                      ) : (
                                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                                      )}
                                    </div>
                                    <div className={`mt-1 text-[10px] ${m.sender==='admin' ? 'text-blue-600 text-right' : 'text-gray-500 text-left'}`}>
                                      {m.sender==='admin'?'Admin':'Visitor'} • {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })
                        )}
                        <div ref={publicMessagesEndRef} />
                      </div>

                      <div className="border-t border-gray-200 p-3">
                        <form onSubmit={sendPublicChatReply} className="flex items-center gap-2">
                          <div className="flex items-center flex-1 bg-white border border-gray-300 rounded-full px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary-500">
                            <button type="button" onClick={onAdminAttachClick} className="mr-2 text-gray-400 hover:text-gray-600" aria-label="Add attachment">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828a4 4 0 10-5.656-5.656L6.343 10.172"/></svg>
                            </button>
                            <input ref={publicFileInputRef} type="file" hidden onChange={(e)=>uploadAdminAttachment(e.target.files?.[0])} />
                            <input
                              value={publicChatReply}
                              onChange={(e)=>setPublicChatReply(e.target.value)}
                              onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); if(publicChatReply.trim()) sendPublicChatReply(e) } }}
                              className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
                              placeholder="Type a reply..."
                            />
                          </div>
                          <button type="submit" className={`h-10 w-10 grid place-items-center rounded-full text-white shadow ${publicChatUploading? 'bg-primary-300 cursor-wait' : publicChatReply.trim()? 'bg-primary-600 hover:bg-primary-700' : 'bg-primary-300 cursor-not-allowed'}`} disabled={publicChatUploading || !publicChatReply.trim()}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                          </button>
                        </form>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Edit Appointment Modal */}
      {showEditAppointmentModal && editingAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Appointment</h2>
                <button onClick={() => { setShowEditAppointmentModal(false); setEditingAppointment(null) }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <form onSubmit={handleSaveAppointment} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input name="name" defaultValue={editingAppointment.name} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input name="email" type="email" defaultValue={editingAppointment.email} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input name="phone" defaultValue={editingAppointment.phone} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                    <input name="service" defaultValue={editingAppointment.service} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input name="date" type="date" defaultValue={editingAppointment.date?.slice(0,10)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot</label>
                    <input name="time_slot" defaultValue={editingAppointment.time_slot} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select name="status" defaultValue={editingAppointment.status} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                      <option value="pending">pending</option>
                      <option value="confirmed">confirmed</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea name="message" defaultValue={editingAppointment.message || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"></textarea>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button type="button" onClick={() => { setShowEditAppointmentModal(false); setEditingAppointment(null) }} className="btn-secondary">Cancel</button>
                  <button type="submit" className="btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {viewInvoice ? (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Invoice Preview</h2>
                    <button onClick={() => { setShowInvoiceModal(false); setViewInvoice(null) }} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p><span className="font-medium">Invoice ID:</span> #{viewInvoice.id?.slice(0,8)}</p>
                    <p><span className="font-medium">Client:</span> {viewInvoice.client_name || 'Unknown'}</p>
                    <p><span className="font-medium">Project:</span> {viewInvoice.project_name || viewInvoice.project_id || 'N/A (Manual Invoice)'}</p>
                    <p><span className="font-medium">Amount:</span> ৳{Number(viewInvoice.amount||0).toLocaleString()}</p>
                    <p><span className="font-medium">Status:</span> {viewInvoice.status || 'issued'}</p>
                    <p><span className="font-medium">Date:</span> {new Date(viewInvoice.created_at||Date.now()).toLocaleString()}</p>
                    <p><span className="font-medium">Due Date:</span> {viewInvoice.due_date ? new Date(viewInvoice.due_date).toLocaleDateString() : 'N/A'}</p>
                    <div className="pt-2 mt-2 border-t">
                      <p><span className="font-medium">Product/Topic:</span> {viewInvoice.subject || '—'}</p>
                      <div className="mt-1">
                        <p className="font-medium">Reason / Details:</p>
                        <p className="ml-4 text-gray-600 whitespace-pre-wrap">{viewInvoice.reason || '—'}</p>
                      </div>
                      {viewInvoice.description ? (
                        <div className="mt-1">
                          <p className="font-medium">Description:</p>
                          <p className="ml-4 text-gray-600 whitespace-pre-wrap">{viewInvoice.description}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-2">
                    <button onClick={() => handleDownloadInvoice(viewInvoice)} className="btn-primary">Download PDF</button>
                    <button onClick={() => handleDeleteInvoice(viewInvoice)} className="btn-danger">Delete</button>
                    <button onClick={() => { setShowInvoiceModal(false); setViewInvoice(null) }} className="btn-secondary">Close</button>
                  </div>
                </>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  
                  if (creatingInvoice) return // Prevent double submission
                  
                  const fd = new FormData(e.currentTarget)
                  const projectId = fd.get('project_id')
                  const amount = parseFloat(fd.get('amount'))

                  // Validation
                  if (!projectId) {
                    alert('Please select a project')
                    return
                  }
                  if (!amount || amount <= 0 || isNaN(amount)) {
                    alert('Please enter a valid amount')
                    return
                  }

                  // Get client_id from selected project
                  const selectedProject = projects.find(p => p.id === projectId)
                  if (!selectedProject || !selectedProject.client_id) {
                    alert('Selected project does not have a client')
                    return
                  }

                  setCreatingInvoice(true)
                  
                  try {
                    // Default due date: 7 days from now
                    const due = new Date()
                    due.setDate(due.getDate() + 7)
                    const dueISO = due.toISOString().split('T')[0]

                    // Create invoice
                    const invoiceData = {
                      client_id: selectedProject.client_id,
                      project_id: projectId,
                      amount,
                      status: 'sent',
                      due_date: dueISO
                    }

                    const { data, error } = await supabase
                      .from('invoices')
                      .insert(invoiceData)
                      .select()
                      .single()
                    
                    if (error) {
                      console.error('Invoice insert error:', error)
                      alert(`Failed to create invoice: ${error.message}`)
                      setCreatingInvoice(false)
                      return
                    }

                    if (data) {
                      // Enrich invoice data for display
                      const enriched = {
                        ...data,
                        client_name: clients.find(c => c.id === selectedProject.client_id)?.name || 'Unknown',
                        project_name: selectedProject.name || 'N/A'
                      }
                      setInvoices(prev => [enriched, ...prev])
                      
                      // Reset form
                      e.currentTarget.reset()
                      
                      // Close modal
                      setShowInvoiceModal(false)
                      
                      // Show success message
                      alert(`Invoice created successfully!`)
                      
                      // Refresh invoices list
                      setDataFetched(false)
                      setTimeout(() => fetchAllData(), 500)
                    }
                  } catch (err) {
                    console.error('Invoice create failed:', err)
                    alert(`Failed to create invoice: ${err.message}`)
                  } finally {
                    setCreatingInvoice(false)
                  }
                }} className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Generate Invoice</h2>
                    <button 
                      onClick={() => { setShowInvoiceModal(false); setViewInvoice(null) }} 
                      type="button" 
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={creatingInvoice}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                    <select 
                      name="project_id" 
                      required
                      disabled={creatingInvoice}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select a project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (৳) *</label>
                    <input 
                      name="amount" 
                      type="number" 
                      step="0.01" 
                      min="0.01" 
                      required
                      disabled={creatingInvoice}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed" 
                      placeholder="Enter amount"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
                    <button 
                      type="button" 
                      onClick={() => { setShowInvoiceModal(false); setViewInvoice(null) }} 
                      disabled={creatingInvoice}
                      className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={creatingInvoice}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {creatingInvoice ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Create Invoice
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Invoice Modal */}
      {showManualInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <form onSubmit={async (e) => {
                e.preventDefault()
                if (creatingManualInvoice) return

                const form = e.currentTarget
                const fd = new FormData(form)
                // Manual-only inputs (no dropdowns)
                const clientNameText = (fd.get('client_name_text') || '').toString().trim()
                const projectNameText = (fd.get('project_name_text') || '').toString().trim()
                const title = (fd.get('title') || '').toString().trim() // product/topic
                const reason = (fd.get('reason') || '').toString().trim()
                const amount = parseFloat(fd.get('amount'))
                const dueDate = fd.get('due_date') || ''

                // Require client name text (manual)
                if (!clientNameText) { alert('Please type client name'); return }
                if (!amount || isNaN(amount) || amount <= 0) { 
                  alert('Enter a valid amount'); 
                  return 
                }
                if (!title) { 
                  alert('Please enter a product/topic'); 
                  return 
                }

                setCreatingManualInvoice(true)
                try {
                  // Default due date: 7 days if empty
                  let due = dueDate
                  if (!due) {
                    const d = new Date()
                    d.setDate(d.getDate() + 7)
                    due = d.toISOString().split('T')[0]
                  }

                  // manual table payload
                  const payload = {
                    client_name: clientNameText,
                    project_name: projectNameText || null,
                    product_subject: title,
                    reason: reason || '',
                    description: '',
                    amount,
                    status: 'sent',
                    due_date: due,
                    created_by: profile?.id || null
                  }

                  const insertRes = await supabase.from('manual_invoices').insert(payload).select().single()
                  if (insertRes.error) throw insertRes.error

                  const data = insertRes.data
                  if (data) {
                    const enriched = { ...data, subject: title }
                    setManualInvoices(prev => [enriched, ...prev])
                    
                    // Safe form reset
                    if (form && typeof form.reset === 'function') {
                      try {
                        form.reset()
                      } catch (resetErr) {
                        console.warn('Form reset failed:', resetErr)
                      }
                    }
                    
                    setShowManualInvoiceModal(false)
                    alert('Manual invoice created successfully!')
                    setDataFetched(false)
                    setTimeout(() => fetchAllData(), 500)
                  }
                } catch (err) {
                  console.error('Manual invoice create failed:', err)
                  alert(`Failed to create manual invoice: ${err.message || 'Unknown error'}`)
                } finally {
                  setCreatingManualInvoice(false)
                }
              }} className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">Manual Invoice</h2>
                  <button onClick={() => setShowManualInvoiceModal(false)} type="button" className="text-gray-400 hover:text-gray-600" disabled={creatingManualInvoice}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                  <input name="client_name_text" type="text" required disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Type client name" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
                  <input name="project_name_text" type="text" disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Type project name (optional)" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product/Topic *</label>
                  <input name="title" type="text" required disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="e.g., Domain Renewal, Hosting, Design Fee" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Details</label>
                  <textarea name="reason" rows="3" disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Brief reason or account head (optional)"></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (৳) *</label>
                  <input name="amount" type="number" step="0.01" min="0.01" required disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Enter amount" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (optional)</label>
                  <input name="due_date" type="date" disabled={creatingManualInvoice} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowManualInvoiceModal(false)} disabled={creatingManualInvoice} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={creatingManualInvoice} className="btn-primary flex items-center gap-2">
                    {creatingManualInvoice ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        Creating...
                      </>
                    ) : (
                      <>Create Manual Invoice</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{editingService ? 'Edit Service' : 'Add Service'}</h2>
                <button onClick={() => { setShowServiceModal(false); setEditingService(null) }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault()
                const fd = new FormData(e.currentTarget)
                const name = fd.get('name')
                const description = fd.get('description')
                const icon = fd.get('icon')
                // Normalize emoji/text to a short, safe string (max 2 glyphs)
                const normalizedIcon = Array.from((icon || '').toString()).slice(0, 2).join('')
                // Normalize description: replace smart punctuation, trim length, collapse spaces
                const normalizeText = (t) => {
                  const str = (t || '').toString()
                    .replace(/[\u2018\u2019]/g, "'")
                    .replace(/[\u201C\u201D]/g, '"')
                    .replace(/[\u2013\u2014]/g, '-')
                    .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, ' ')
                    .replace(/[\r\t]+/g, ' ')
                    .replace(/\s{2,}/g, ' ')
                    .trim()
                  return str.length > 1000 ? str.slice(0, 1000) : str
                }
                const safeDescription = normalizeText(description)
                const payload = { name, description: safeDescription, icon: normalizedIcon }
                try {
                  setSavingService(true)
                  // guard: admin only
                  if (profile?.role !== 'admin') {
                    alert('Only admins can create services')
                    setSavingService(false)
                    return
                  }
                  console.log('🛠️ Saving service payload:', payload)

                  // Skip preflight/timeouts; execute writes directly to surface real errors immediately

                  console.log('🧾 Services payload:', payload)
                  if (editingService) {
                    console.log('✏️ Updating service id:', editingService.id)
                    const { error } = await supabase
                      .from('services')
                      .update(payload, { returning: 'minimal' })
                      .eq('id', editingService.id)
                    // Retry without icon if it failed (emoji/encoding/length issues)
                    if (error) {
                      console.warn('Update failed, retrying without icon…', error)
                      const { error: retryErr } = await supabase
                        .from('services')
                        .update({ name, description: normalizeText(description), icon: null }, { returning: 'minimal' })
                        .eq('id', editingService.id)
                      if (retryErr) {
                        console.error('Service update error:', retryErr)
                        // Final fallback: ASCII-only shortened description
                        const asciiDesc = normalizeText(description).replace(/[^\x20-\x7E]+/g, '')
                        const { error: retry2 } = await supabase
                          .from('services')
                          .update({ name, description: asciiDesc.slice(0, 500), icon: null }, { returning: 'minimal' })
                          .eq('id', editingService.id)
                        if (retry2) {
                          alert(`Failed to update: ${retryErr.message || retry2.message || 'Unknown error'}`)
                          throw retry2
                        }
                        alert(`Failed to update: ${retryErr.message || 'Unknown error'}`)
                        
                      }
                    }
                    {
                      // Refetch list after update
                      const { data: svcLatest } = await supabase
                        .from('services')
                        .select('*')
                        .order('created_at', { ascending: false })
                        .limit(50)
                      if (svcLatest) setServicesList(svcLatest)
                      alert('Service updated successfully')
                    }
                  } else {
                    console.log('➕ Inserting new service')
                    const { error } = await supabase
                      .from('services')
                      .insert(payload, { returning: 'minimal' })
                    if (error) {
                      console.warn('Insert failed, retrying without icon…', error)
                      const { error: retryErr } = await supabase
                        .from('services')
                        .insert({ name, description: normalizeText(description), icon: null }, { returning: 'minimal' })
                      if (retryErr) {
                        console.error('Service insert error:', retryErr)
                        // Final fallback: ASCII-only shortened description
                        const asciiDesc = normalizeText(description).replace(/[^\x20-\x7E]+/g, '')
                        const { error: retry2 } = await supabase
                          .from('services')
                          .insert({ name, description: asciiDesc.slice(0, 500), icon: null }, { returning: 'minimal' })
                        if (retry2) {
                          alert(`Failed to create: ${retryErr.message || retry2.message || 'Unknown error'}`)
                          throw retry2
                        }
                        alert(`Failed to create: ${retryErr.message || 'Unknown error'}`)
                      }
                    }
                    {
                      // Refetch list after create
                      const { data: svcLatest } = await supabase
                        .from('services')
                        .select('*')
                        .order('created_at', { ascending: false })
                        .limit(50)
                      if (svcLatest) setServicesList(svcLatest)
                      alert('Service created successfully')
                    }
                  }
                  // close modal after successful attempt
                  setShowServiceModal(false)
                  setEditingService(null)
                } catch (err) {
                  console.error('Service save failed:', err)
                  const msg = err?.message || String(err)
                  alert(`Failed to save service: ${msg}`)
                } finally {
                  setSavingService(false)
                }
              }} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                  <input name="name" defaultValue={editingService?.name || ''} required onPaste={handlePasteToInput('text')} onInput={handleNormalizeOnInput('text')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea name="description" defaultValue={editingService?.description || ''} rows={3} onPaste={handlePasteToInput('text')} onInput={handleNormalizeOnInput('text')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon (emoji or short text)</label>
                  <input 
                    name="icon" 
                    defaultValue={editingService?.icon || ''} 
                    onPaste={handleEmojiPaste((next)=>next, 'icon')} 
                    onInput={(e)=>{
                      // keep at most 2 emoji/characters without stripping
                      const limited = Array.from(e.currentTarget.value).slice(0,2).join('')
                      if (limited !== e.currentTarget.value) {
                        e.currentTarget.value = limited
                      }
                    }}
                    autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" 
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button type="button" onClick={() => { setShowServiceModal(false); setEditingService(null) }} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={savingService} className={`btn-primary ${savingService ? 'opacity-70 cursor-not-allowed' : ''}`}>{savingService ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
                <button
                  onClick={() => setShowEditUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={editUser.name}
                    onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={editUser.email}
                    onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={editUser.role}
                    onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="client">Client</option>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editUser.phone}
                    onChange={(e) => setEditUser({...editUser, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={editUser.company}
                    onChange={(e) => setEditUser({...editUser, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={editUser.address}
                    onChange={(e) => setEditUser({...editUser, address: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={editUser.bio}
                    onChange={(e) => setEditUser({...editUser, bio: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter bio or description"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditUserModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Updating...' : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New User</h2>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter valid email address"
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be a valid email format</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter password (min 6 characters)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="client">Client</option>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={newUser.company}
                    onChange={(e) => setNewUser({...newUser, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={newUser.address}
                    onChange={(e) => setNewUser({...newUser, address: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={newUser.bio}
                    onChange={(e) => setNewUser({...newUser, bio: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter bio or description"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Product</h2>
                <button
                  onClick={() => setShowAddProductModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddProduct} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'name', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'description', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter product description"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (৳) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'price', 'price')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter price"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="website">Website</option>
                    <option value="mobile">Mobile App</option>
                    <option value="design">UI/UX Design</option>
                    <option value="marketing">Digital Marketing</option>
                    <option value="plugin">Plugin</option>
                    <option value="api">API</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
                  <div className="space-y-3">
                    {/* Image Upload */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], false)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <p className="text-sm text-blue-600 mt-1">Uploading image...</p>
                      )}
                    </div>
                    
                    {/* Image URL Input */}
                    <div>
                      <input
                        type="text"
                        value={newProduct.image_url}
                        onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})}
                        onPaste={handlePastePlainText(setNewProduct, 'image_url', 'url')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Or enter image URL directly"
                      />
                    </div>
                    
                    {/* Image Preview */}
                    {newProduct.image_url && (
                      <div className="mt-2">
                        <img 
                          src={newProduct.image_url} 
                          alt="Preview" 
                          className="w-32 h-32 object-cover rounded-lg border"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Live Demo Link</label>
                  <input
                    type="text"
                    value={newProduct.live_link}
                    onChange={(e) => setNewProduct({...newProduct, live_link: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'live_link', 'url')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.com/demo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
                  <textarea
                    value={newProduct.specifications}
                    onChange={(e) => setNewProduct({...newProduct, specifications: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'specifications', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Technical specifications, requirements, etc."
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Features</label>
                  <textarea
                    value={newProduct.key_features}
                    onChange={(e) => setNewProduct({...newProduct, key_features: e.target.value})}
                    onPaste={handlePastePlainText(setNewProduct, 'key_features', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Main features, benefits, highlights"
                    rows="3"
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">HTML Page Upload</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload HTML Page</label>
                    <input
                      type="file"
                      accept=".html"
                      onChange={(e) => handleHtmlUpload(e.target.files[0], false)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={uploadingHtml}
                    />
                    {uploadingHtml && (
                      <p className="text-sm text-blue-600 mt-1">Uploading HTML file...</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Upload an HTML file to display detailed product information</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">3rd Party Purchase Option</h3>
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">3rd Party Purchase Link</label>
                    <input
                      type="text"
                      value={newProduct.third_party_link}
                      onChange={(e) => setNewProduct({...newProduct, third_party_link: e.target.value})}
                      onPaste={handlePastePlainText(setNewProduct, 'third_party_link', 'url')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/buy"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">3rd Party Price (৳)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newProduct.third_party_price}
                      onChange={(e) => setNewProduct({...newProduct, third_party_price: e.target.value})}
                      onPaste={handlePastePlainText(setNewProduct, 'third_party_price', 'price')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter 3rd party price"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newProduct.is_active}
                      onChange={(e) => setNewProduct({...newProduct, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active Product</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddProductModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingProduct}
                    className="btn-primary"
                  >
                    {savingProduct ? 'Adding...' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Product</h2>
                <button
                  onClick={() => setShowEditProductModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdateProduct} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={editProduct.name}
                    onChange={(e) => setEditProduct({...editProduct, name: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'name', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    value={editProduct.description}
                    onChange={(e) => setEditProduct({...editProduct, description: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'description', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter product description"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (৳) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={editProduct.price}
                    onChange={(e) => setEditProduct({...editProduct, price: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'price', 'price')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter price"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={editProduct.category}
                    onChange={(e) => setEditProduct({...editProduct, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="website">Website</option>
                    <option value="mobile">Mobile App</option>
                    <option value="design">UI/UX Design</option>
                    <option value="marketing">Digital Marketing</option>
                    <option value="plugin">Plugin</option>
                    <option value="api">API</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
                  <div className="space-y-3">
                    {/* Image Upload */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0], true)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <p className="text-sm text-blue-600 mt-1">Uploading image...</p>
                      )}
                    </div>
                    
                    {/* Image URL Input */}
                    <div>
                    <input
                      type="text"
                        value={editProduct.image_url}
                        onChange={(e) => setEditProduct({...editProduct, image_url: e.target.value})}
                        onPaste={handlePastePlainText(setEditProduct, 'image_url', 'url')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Or enter image URL directly"
                      />
                    </div>
                    
                    {/* Image Preview */}
                    {editProduct.image_url && (
                      <div className="mt-2">
                        <img 
                          src={editProduct.image_url} 
                          alt="Preview" 
                          className="w-32 h-32 object-cover rounded-lg border"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Live Demo Link</label>
                  <input
                    type="text"
                    value={editProduct.live_link}
                    onChange={(e) => setEditProduct({...editProduct, live_link: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'live_link', 'url')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.com/demo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
                  <textarea
                    value={editProduct.specifications}
                    onChange={(e) => setEditProduct({...editProduct, specifications: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'specifications', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Technical specifications, requirements, etc."
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Features</label>
                  <textarea
                    value={editProduct.key_features}
                    onChange={(e) => setEditProduct({...editProduct, key_features: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'key_features', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Main features, benefits, highlights"
                    rows="3"
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">HTML Page Upload</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload HTML Page</label>
                    <input
                      type="file"
                      accept=".html"
                      onChange={(e) => handleHtmlUpload(e.target.files[0], true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={uploadingHtml}
                    />
                    {uploadingHtml && (
                      <p className="text-sm text-blue-600 mt-1">Uploading HTML file...</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Upload an HTML file to display detailed product information</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">3rd Party Purchase Option</h3>
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">3rd Party Purchase Link</label>
                    <input
                      type="text"
                      value={editProduct.third_party_link}
                      onChange={(e) => setEditProduct({...editProduct, third_party_link: e.target.value})}
                      onPaste={handlePastePlainText(setEditProduct, 'third_party_link', 'url')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/buy"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">3rd Party Price (৳)</label>
                  <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editProduct.third_party_price}
                    onChange={(e) => setEditProduct({...editProduct, third_party_price: e.target.value})}
                    onPaste={handlePastePlainText(setEditProduct, 'third_party_price', 'price')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter 3rd party price"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editProduct.is_active}
                      onChange={(e) => setEditProduct({...editProduct, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active Product</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditProductModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingProduct}
                    className="btn-primary"
                  >
                    {savingProduct ? 'Updating...' : 'Update Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Project Creation Modal */}
      {showCreateProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isEditMode ? 'Edit Project' : 'Create New Project'}
                </h2>
                <button
                  onClick={() => resetProjectForm()}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateProject} noValidate className="space-y-6">
                {/* Project Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    onPaste={handlePastePlainText(setNewProject, 'name', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter project name"
                  />
                </div>

                {/* Project Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Description *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    onPaste={handlePastePlainText(setNewProject, 'description', 'text')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Describe the project details..."
                  />
                </div>

                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Client *
                  </label>
                  <select
                    required
                    value={newProject.client_id}
                    onChange={(e) => setNewProject({...newProject, client_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Choose a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} ({client.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Employees
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {employees.length === 0 ? (
                      <p className="text-gray-500 text-sm">No employees available</p>
                    ) : (
                      employees.map(employee => (
                        <label key={employee.id} className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={newProject.selectedEmployees.includes(employee.id)}
                            onChange={(e) => {
                              console.log('🔘 Employee checkbox changed:', employee.name, 'checked:', e.target.checked)
                              console.log('📋 Current selectedEmployees:', newProject.selectedEmployees)
                              
                              if (e.target.checked) {
                                const newSelected = [...newProject.selectedEmployees, employee.id]
                                console.log('✅ Adding employee, new list:', newSelected)
                                setNewProject({
                                  ...newProject,
                                  selectedEmployees: newSelected
                                })
                              } else {
                                const newSelected = newProject.selectedEmployees.filter(id => id !== employee.id)
                                console.log('❌ Removing employee, new list:', newSelected)
                                setNewProject({
                                  ...newProject,
                                  selectedEmployees: newSelected
                                })
                              }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {employee.name} ({employee.email})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Budget (৳)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newProject.budget}
                    onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                    onPaste={handlePastePlainText(setNewProject, 'budget', 'price')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter project budget"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newProject.start_date}
                    onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newProject.end_date}
                    onChange={(e) => setNewProject({...newProject, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={newProject.priority}
                    onChange={(e) => setNewProject({...newProject, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={() => resetProjectForm()}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingProject}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creatingProject ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Project' : 'Create Project')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard